import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AdminIdRow from '@/components/AdminIdRow';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Menu, X, Eye, Copy, QrCode, Send, Check, GripVertical,
  Pencil, Trash2, Plus, Star, Mic, Type, Hash, CheckSquare,
  ListChecks, Phone, ChevronRight, ChevronLeft, Smile,
  ToggleLeft, MessageSquare, Layout, Download, Search,
  ChevronDown, AlertCircle, Zap, ArrowLeft, Settings as SettingsIcon,
  Tag, Shield, AlignLeft, FileText, CircleDot, Grid3X3,
  Gauge, Calendar as CalendarIcon, Clock, Upload, Image as ImageIcon,
  CheckCircle, Bold, Italic, Underline, ChevronsUpDown, Archive, GitBranch,
  List as ListIcon, ListOrdered, Video, Film, Square, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { computeQuestionNumbers } from '@/lib/questionNumbering';
import { sanitizeHtml } from '@/lib/sanitize';
import { cleanQuestionLabel } from '@/lib/answerFormatting';
import { NpsFace, resolveNpsPresentation } from '@/components/NpsFace';
import { SurveyExperienceSetting, experienceLabel, resolveExperience, isMysteryShoppingSurvey } from '@/components/SurveyExperienceSetting';
import { SurveyAccessSetting } from '@/components/surveyAccess/SurveyAccessSetting';
import { PublicEntrySetting } from '@/components/surveyAccess/PublicEntrySetting';
import { AttributeRatingsSettings, ContactPreferenceSettings } from '@/components/surveyBuilder/Phase4aQuestionSettings';
import ClassificationSettings, { CLASSIFIABLE_TYPES } from '@/components/surveyBuilder/ClassificationSettings';
import ChoiceFollowupSettings from '@/components/surveyBuilder/ChoiceFollowupSettings';
import ThankYouStaticImage from '@/components/surveyBuilder/ThankYouStaticImage';
import { REDIRECT_CONDITIONS, effectiveRedirectCondition } from '@/lib/redirectSettings';
import { remapFollowups } from '@/lib/choiceFollowups';

// Phase 4A type-specific fields carried from palette defaults on create.
const PHASE4A_FIELDS = ['attributes', 'attr_instruction', 'response_mode', 'contact_preference_settings', 'option_followups'];

// ─── HELPERS ───
const stripHtml = (html) => {
  if (html == null) return '';
  if (typeof document === 'undefined') return String(html);
  const tmp = document.createElement('div');
  tmp.innerHTML = String(html);
  return tmp.innerText || tmp.textContent || '';
};

// ─── QUESTION TYPE DEFINITIONS ───
const QUESTION_TYPES = [
  { group: 'Scale & Rating', items: [
    { type: 'nps', label: 'Net Promoter\u00AE Score', icon: Hash, color: '#3B82F6', isQuestion: true, defaults: { text: 'How likely are you to recommend us?', is_required: true, nps_display_style: 'faces_numbers', nps_face_appearance: 'color' } },
    { type: 'star_rating', label: 'Star Rating', icon: Star, color: '#F59E0B', isQuestion: true, defaults: { text: 'Rate your experience', is_required: true, star_settings: { max_stars: 5, label: '' } } },
    { type: 'rating_scale', label: 'Rating Scale', icon: Gauge, color: '#F59E0B', isQuestion: true, defaults: { text: 'How satisfied are you?', is_required: true, rating_scale: { min_value: 1, max_value: 5, min_label: 'Very Dissatisfied', max_label: 'Very Satisfied' } } },
    { type: 'emoji_rating', label: 'Emoji / Smiley Rating', icon: Smile, color: '#F59E0B', isQuestion: true, defaults: { text: 'How did we do?', is_required: true, emoji_settings: { scale: 5, labels: [] } } },
    { type: 'attribute_ratings', label: 'Grouped Attribute Rating', icon: ListChecks, color: '#F59E0B', isQuestion: true, keepDefaultText: true, defaults: { text: 'Please rate the following.', is_required: true, attr_instruction: 'Tap the stars for each item.', attributes: [{ id: 'attr_1', label: 'Criterion 1' }, { id: 'attr_2', label: 'Criterion 2' }, { id: 'attr_3', label: 'Criterion 3' }] } },
    { type: 'yes_no', label: 'Yes or No', icon: ToggleLeft, color: '#22C55E', isQuestion: true, defaults: { text: 'Your question here', is_required: true, options: [{ label: 'Yes', score_value: 1 }, { label: 'No', score_value: 0 }] } },
  ]},
  { group: 'Choice', items: [
    { type: 'dropdown', label: 'Dropdown', icon: ChevronsUpDown, color: '#22C55E', isQuestion: true, defaults: { text: 'Choose one option', is_required: true, options: [{ label: 'Option 1' }, { label: 'Option 2' }, { label: 'Option 3' }] } },
    { type: 'single_choice', label: 'Single Choice', icon: CircleDot, color: '#22C55E', isQuestion: true, defaults: { text: 'Choose one option', is_required: true, options: [{ label: 'Option 1', score_value: null }, { label: 'Option 2', score_value: null }] } },
    { type: 'multiple_choice', label: 'Multiple Choice', icon: CheckSquare, color: '#22C55E', isQuestion: true, defaults: { text: 'Select all that apply', is_required: false, options: [{ label: 'Option 1', score_value: null }, { label: 'Option 2', score_value: null }] } },
    { type: 'button_choice', label: 'Button Choice', icon: Grid3X3, color: '#22C55E', isQuestion: true, defaults: { text: 'Tap one', is_required: true, options: [{ label: 'Option 1', score_value: null }, { label: 'Option 2', score_value: null }] } },
    { type: 'image_choice', label: 'Image Choice', icon: ImageIcon, color: '#22C55E', isQuestion: true, defaults: { text: 'Choose one', is_required: true, image_choice_settings: { mode: 'single' }, options: [{ id: 'opt_1', label: 'Option 1', image_url: '' }, { id: 'opt_2', label: 'Option 2', image_url: '' }] } },
    { type: 'ranking', label: 'Ranking', icon: ListOrdered, color: '#22C55E', isQuestion: true, defaults: { text: 'Rank in order of importance', is_required: true, ranking_settings: {}, options: [{ id: 'opt_1', label: 'Option 1' }, { id: 'opt_2', label: 'Option 2' }, { id: 'opt_3', label: 'Option 3' }] } },
  ]},
  { group: 'Text & Input', items: [
    { type: 'short_text', label: 'Short Text', icon: AlignLeft, color: '#64748B', isQuestion: true, defaults: { text: 'Your question here', is_required: false, placeholder_text: 'Type your answer' } },
    { type: 'email', label: 'Email', icon: Mail, color: '#64748B', isQuestion: true, defaults: { text: 'What is your email address?', is_required: false, placeholder_text: 'name@example.com' } },
    { type: 'long_text', label: 'Long Text', icon: FileText, color: '#64748B', isQuestion: true, defaults: { text: 'Your question here', is_required: false, placeholder_text: 'Share your thoughts' } },
    { type: 'number_input', label: 'Number Input', icon: Hash, color: '#64748B', isQuestion: true, defaults: { text: 'Enter a number', is_required: false, placeholder_text: '0', number_settings: { min_value: null, max_value: null, step: 1 } } },
    { type: 'voice', label: 'Voice Response', icon: Mic, color: '#EF4444', isQuestion: true, defaults: { text: 'Tell us about your experience', is_required: false, voice_settings: { max_duration_seconds: 120, allow_text_fallback: true } } },
    { key: 'text_voice', type: 'voice', label: 'Text + Optional Voice', icon: MessageSquare, color: '#EF4444', isQuestion: true, keepDefaultText: true, defaults: { text: 'What is the primary reason for your answer?', is_required: false, response_mode: 'text_first', voice_settings: { max_duration_seconds: 120, allow_text_fallback: true } } },
    { type: 'mobile_or_email', label: 'Mobile or Email', icon: Mail, color: '#64748B', isQuestion: true, keepDefaultText: true, defaults: { text: 'Please enter your mobile number or email', is_required: false } },
  ]},
  { group: 'Media', items: [
    { type: 'media_question', label: 'Media Question', icon: Film, color: '#8B5CF6', isQuestion: true, defaults: { text: 'Listen and respond', is_required: false, media_settings: { kind: 'audio', media_url: '', answer_format: 'long_text' } } },
  ]},
  { group: 'Advanced', items: [
    { type: 'date', label: 'Date', icon: CalendarIcon, color: '#3B82F6', isQuestion: true, defaults: { text: 'Pick a date', is_required: false } },
    { type: 'time', label: 'Time', icon: Clock, color: '#3B82F6', isQuestion: true, defaults: { text: 'Pick a time', is_required: false } },
    { type: 'file_upload', label: 'File Upload', icon: Upload, color: '#8B5CF6', isQuestion: true, defaults: { text: 'Upload a file', is_required: false, file_settings: { accepted: 'any', max_size_mb: 5, max_files: 1 } } },
    { type: 'confirmation', label: 'Confirmation', icon: CheckCircle, color: '#22C55E', isQuestion: true, defaults: { text: 'I understand and agree', is_required: true, confirmation_settings: { button_label: 'I Agree' } } },
    { type: 'contact_preference', label: 'Contact Preference', icon: Phone, color: '#64748B', isQuestion: true, keepDefaultText: true, defaults: { text: 'Would you like someone from [Brand Name] to contact you about your experience?', is_required: false, contact_preference_settings: { linked_question_id: null, ask_name_on_yes: false, require_name_on_yes: true } } },
    { type: 'contact_info', label: 'Contact Information', icon: Phone, color: '#64748B', isQuestion: true, defaults: { text: 'Your contact information', is_required: false, contact_fields: { mobile: { enabled: true, required: false }, email: { enabled: false, required: false }, first_name: { enabled: false, required: false }, last_name: { enabled: false, required: false } } } },
  ]},
  { group: 'Matrix & Grid', items: [
    { type: 'matrix', label: 'Matrix', icon: Grid3X3, color: '#0EA5E9', isQuestion: true, defaults: { text: 'Rate each item', is_required: true, items_source: 'fixed', items: ['Item 1', 'Item 2'], criteria: ['Quality'], scale: 'excellent_poor', custom_scale: ['Excellent', 'Good', 'Average', 'Poor'] } },
  ]},
  { group: 'Layout & Content', items: [
    { type: 'welcome', label: 'Welcome Screen', icon: ImageIcon, color: '#1A3E6F', isQuestion: false, defaults: { text: 'Welcome', sub_text: '', is_required: false, welcome_settings: { title: 'Welcome', subtitle: 'Thanks for taking a moment to share your feedback.', button_label: 'Start' }, welcome_image_url: '' } },
    { type: 'section_header', label: 'Section Header', icon: Layout, color: '#94A3B8', isQuestion: false, defaults: { text: 'Section Title', sub_text: '', is_required: false } },
    { type: 'rich_text_block', label: 'Rich Text Block', icon: Type, color: '#94A3B8', isQuestion: false, defaults: { text: '', is_required: false, rich_content: '<p>Write formatted content here…</p>' } },
    { type: 'image_display', label: 'Image Display', icon: ImageIcon, color: '#94A3B8', isQuestion: false, defaults: { text: '', is_required: false, image_settings: { url: '', alt: '' } } },
    { type: 'thank_you', label: 'Thank You Screen', icon: CheckCircle, color: '#1A3E6F', isQuestion: false, defaults: { text: 'Thank you', is_required: false, thank_you_settings: { message: 'Thanks for your feedback!', show_review_link: 'never' } } },
  ]},
];

const NON_QUESTION_TYPES = new Set(['section_header', 'rich_text_block', 'image_display', 'welcome', 'thank_you']);
const CHOICE_TYPES = new Set(['single_choice', 'multiple_choice', 'button_choice', 'dropdown']);
// Types eligible for Experience Performance question distributions and
// therefore eligible for the "Administrative / report-identification"
// toggle. Mirrors backend `_EP_ELIGIBLE_TYPES` (choice + yes/no).
const ADMIN_TAGGABLE_TYPES = new Set(['single_choice', 'multiple_choice', 'button_choice', 'dropdown', 'yes_no']);
const SCORED_TYPES = new Set(['nps', 'star_rating', 'rating_scale', 'yes_no', 'single_choice', 'multiple_choice', 'button_choice', 'matrix', 'dropdown', 'emoji_rating']);
const PLACEHOLDER_TYPES = new Set(['short_text', 'long_text', 'number_input', 'email', 'mobile_or_email']);
const COMMENTABLE_TYPES = new Set(['yes_no', 'button_choice', 'single_choice', 'multiple_choice', 'dropdown', 'star_rating', 'rating_scale']);

const getTypeConfig = (type, q) => {
  // Text + Optional Voice is a `voice` question in text-first mode.
  const key = type === 'voice' && q?.response_mode === 'text_first' ? 'text_voice' : type;
  for (const g of QUESTION_TYPES) {
    const found = g.items.find(i => (i.key || i.type) === key);
    if (found) return found;
  }
  return { label: type, icon: MessageSquare, color: '#64748B' };
};

// ─── MAIN COMPONENT ───
export default function SurveyBuilder() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQ, setSelectedQ] = useState(null);
  const [leftOpen, setLeftOpen] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCodes, setQrCodes] = useState([]);
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [allBranches, setAllBranches] = useState([]);
  const [branchSearch, setBranchSearch] = useState('');
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [assigningBranches, setAssigningBranches] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [planCaps, setPlanCaps] = useState(null);
  const [deleteQId, setDeleteQId] = useState(null);
  const [gapCategories, setGapCategories] = useState([]);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragType, setDragType] = useState(null); // 'library' or 'reorder'
  const dragDataRef = useRef(null);
  const nameInputRef = useRef(null);

  // ─── FETCH ───
  const fetchSurvey = useCallback(async () => {
    try {
      const res = await fetch(`${API}/surveys/builder/${surveyId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSurvey(data);
        setQuestions(data.questions || []);
        if (data.status === 'active') setPublished(true);
      } else {
        toast.error('Survey not found');
        navigate('/surveys');
      }
    } catch (err) {
      toast.error('Failed to load survey');
    } finally {
      setLoading(false);
    }
  }, [surveyId, navigate]);

  useEffect(() => { fetchSurvey(); }, [fetchSurvey]);

  // Fetch plan caps + branches once
  useEffect(() => {
    fetch(`${API}/surveys/plan-caps`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPlanCaps(d); })
      .catch(() => {});
    fetch(`${API}/branches`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setAllBranches(d); })
      .catch(() => {});
    fetch(`${API}/gaps/categories`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.categories) setGapCategories(d.categories); })
      .catch(() => {});
  }, []);

  // ─── API HELPERS ───
  const apiPut = async (path, body) => {
    const res = await fetch(`${API}${path}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
    return res;
  };

  const apiPost = async (path, body) => {
    const res = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body || {}) });
    return res;
  };

  const apiDelete = async (path) => {
    const res = await fetch(`${API}${path}`, { method: 'DELETE', credentials: 'include' });
    return res;
  };

  // ─── SURVEY ACTIONS ───
  const updateSurveyName = async (name) => {
    await apiPut(`/surveys/builder/${surveyId}`, { name });
    setSurvey(prev => ({ ...prev, name }));
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await apiPost(`/surveys/builder/${surveyId}/publish`);
      if (res.ok) {
        setPublished(true);
        setSurvey(prev => ({ ...prev, status: 'active' }));
        toast.success('Survey published!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to publish');
      }
    } catch { toast.error('Failed to publish'); }
    finally { setPublishing(false); }
  };

  const handleDuplicate = async () => {
    try {
      const res = await apiPost(`/surveys/builder/${surveyId}/duplicate`);
      if (res.ok) {
        const data = await res.json();
        toast.success('Survey duplicated!');
        navigate(`/survey-builder/${data.survey_id}`);
      }
    } catch { toast.error('Failed to duplicate'); }
  };

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // Question-type picker (opened by the bottom Add Question button)
  const [pickerOpen, setPickerOpen] = useState(false);
  const handleArchive = async () => {
    setArchiving(true);
    try {
      const res = await apiPost(`/surveys/builder/${surveyId}/archive`);
      if (res.ok) {
        const data = await res.json();
        setSurvey(prev => ({ ...(prev || {}), ...data }));
        setPublished(false);
        toast.success('Survey archived');
        setArchiveDialogOpen(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to archive');
      }
    } catch { toast.error('Failed to archive'); }
    finally { setArchiving(false); }
  };

  // ─── QUESTION ACTIONS ───
  const addQuestion = async (type, defaults = {}) => {
    try {
      const body = { type, text: defaults.text || 'New question', is_required: defaults.is_required ?? false, options: defaults.options || [], voice_settings: defaults.voice_settings || null, star_settings: defaults.star_settings || null, thank_you_settings: defaults.thank_you_settings || null, media_settings: defaults.media_settings || null, nps_display_style: defaults.nps_display_style || null, nps_face_appearance: defaults.nps_face_appearance || null };
      for (const k of PHASE4A_FIELDS) if (defaults[k] != null) body[k] = defaults[k];
      const res = await apiPost(`/surveys/builder/${surveyId}/questions`, body);
      if (res.ok) {
        const q = await res.json();
        setQuestions(prev => [...prev, q]);
        setSelectedQ(q.id);
        toast.success('Question added');
      } else {
        // Surface backend rejections (e.g. plan question-count limit reached)
        // instead of failing silently. Some browsers/proxies consume the error
        // body before user code can read it, so fall back to a status-aware
        // message that still tells the user *why* it failed.
        let detail = null;
        try { detail = (await res.json())?.detail; } catch { /* body unreadable */ }
        if (!detail) {
          // Only synthesise a quota message when the backend body was
          // unreadable. The backend itself emits a precise message when
          // the quota is genuinely hit ("You have reached the question
          // limit for your plan…"), which we always prefer when present.
          if (res.status === 400) detail = "Could not add this question. Please check your plan or the question configuration.";
          else if (res.status === 401) detail = 'Your session has expired. Please log in again.';
          else if (res.status === 404) detail = 'Survey not found.';
          else if (res.status >= 500) detail = 'Server error while adding the question. Please try again.';
        }
        toast.error(detail || 'Failed to add question');
      }
    } catch { toast.error('Failed to add question'); }
  };

  const updateQuestion = async (questionId, updates) => {
    try {
      const res = await apiPut(`/surveys/builder/${surveyId}/questions/${questionId}`, updates);
      if (res.ok) {
        const updated = await res.json();
        setQuestions(prev => prev.map(q => q.id === questionId ? updated : q));
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(typeof d.detail === 'string' ? d.detail : 'Failed to update question');
      }
    } catch { toast.error('Failed to update question'); }
  };

  const deleteQuestion = async (questionId) => {
    try {
      const res = await apiDelete(`/surveys/builder/${surveyId}/questions/${questionId}`);
      if (res.ok) {
        setQuestions(prev => prev.filter(q => q.id !== questionId));
        if (selectedQ === questionId) setSelectedQ(null);
        toast.success('Question deleted');
      }
    } catch { toast.error('Failed to delete'); }
  };

  const duplicateQuestion = async (question) => {
    await addQuestion(question.type, { text: `${question.text} (copy)`, is_required: question.is_required, options: question.options, voice_settings: question.voice_settings, star_settings: question.star_settings, thank_you_settings: question.thank_you_settings, attributes: question.attributes, attr_instruction: question.attr_instruction, response_mode: question.response_mode, option_followups: question.option_followups });
  };

  const reorderQuestions = async (newOrder) => {
    const ids = newOrder.map(q => q.id);
    setQuestions(newOrder.map((q, i) => ({ ...q, order: i })));
    try {
      await apiPut(`/surveys/builder/${surveyId}/questions/reorder`, { question_ids: ids });
    } catch { toast.error('Failed to reorder'); fetchSurvey(); }
  };

  // ─── DRAG & DROP: Library → Canvas ───
  const onLibraryDragStart = (e, typeConfig) => {
    setDragType('library');
    dragDataRef.current = typeConfig;
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', typeConfig.type);
  };

  // ─── DRAG & DROP: Reorder ───
  const onReorderDragStart = (e, index) => {
    setDragType('reorder');
    dragDataRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const onCanvasDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragType === 'library' ? 'copy' : 'move';
    setDragOverIndex(index);
  };

  const onCanvasDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    // Consume drag state synchronously so a bubbled drop on a parent wrapper
    // can't re-trigger this handler with the same payload.
    const consumedType = dragType;
    const consumedData = dragDataRef.current;
    if (consumedData == null) return;
    dragDataRef.current = null;
    setDragType(null);
    if (consumedType === 'library') {
      await addQuestion(consumedData.type, consumedData.defaults);
    } else if (consumedType === 'reorder') {
      const fromIndex = consumedData;
      if (fromIndex === dropIndex) return;

      const dragged = questions[fromIndex];
      // Section group drag: when the user drags a section_header, move the
      // header AND every question that belongs to that section (i.e. every
      // item until the next section_header or end of survey) as a single
      // group. Individual non-section questions still move on their own.
      if (dragged?.type === 'section_header') {
        let groupEnd = questions.length;
        for (let i = fromIndex + 1; i < questions.length; i++) {
          if (questions[i].type === 'section_header') { groupEnd = i; break; }
        }
        const groupSize = groupEnd - fromIndex;
        // No-op: dropping inside the group's own original range.
        if (dropIndex >= fromIndex && dropIndex < groupEnd) return;
        const newQ = [...questions];
        const removed = newQ.splice(fromIndex, groupSize);
        // If the drop target sat after the original group, account for the
        // items we just removed before re-inserting.
        const adjustedDrop = dropIndex > fromIndex ? dropIndex - groupSize : dropIndex;
        newQ.splice(adjustedDrop, 0, ...removed);
        await reorderQuestions(newQ);
      } else {
        const newQ = [...questions];
        const [moved] = newQ.splice(fromIndex, 1);
        newQ.splice(dropIndex, 0, moved);
        await reorderQuestions(newQ);
      }
    }
  };

  const onCanvasDragEnd = () => { setDragOverIndex(null); setDragType(null); dragDataRef.current = null; };

  // ─── QR & BRANCH ───
  const fetchQrCodes = async () => {
    const data = await (await fetch(`${API}/surveys/builder/${surveyId}`, { credentials: 'include' })).json();
    if (data.assigned_branches?.length) {
      const codes = await (await fetch(`${API}/surveys/builder/${surveyId}`, { credentials: 'include' })).json();
      // Fetch QR codes from the survey_qr_codes via a workaround — re-assign to get them
      // Actually we need to fetch them. Let's use the assign endpoint response cached approach
      setQrCodes([]); // placeholder, will be populated by assign
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetch(`${API}/branches`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllBranches(data || []);
      }
    } catch (e) { console.error('Failed to fetch branches:', e); }
  };

  const openQrModal = async () => {
    setQrModalOpen(true);
    // Fetch existing QR codes for this survey. Use `cache: 'no-store'` so the
    // browser's HTTP cache can't hand us a stale response from before the
    // domain rename, and force the POST re-assign path which server-side
    // re-heals any qr_code_url that doesn't match the current public base URL.
    try {
      const res = await fetch(`${API}/surveys/builder/${surveyId}`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (res.ok) {
        const data = await res.json();
        // Fetch QR code images for assigned branches
        const assignedBranches = data.assigned_branches || [];
        if (assignedBranches.length > 0) {
          // Re-assign to get fresh QR code data back (server regenerates PNG
          // on disk if the stored URL no longer matches PUBLIC_APP_URL).
          const qrRes = await apiPost(`/surveys/builder/${surveyId}/assign-branches`, { branch_ids: assignedBranches });
          if (qrRes.ok) {
            const qrData = await qrRes.json();
            // Stamp each QR record with a cache-buster so <img src> is treated
            // as a new URL by the browser and the PNG gets re-downloaded even
            // when the on-disk filename stayed the same.
            const bust = Date.now();
            const fresh = (qrData.qr_codes || []).map(q => ({ ...q, _cacheBust: bust }));
            setQrCodes(fresh);
          }
        } else {
          setQrCodes([]);
        }
      }
    } catch { /* ignore */ }
  };

  const openBranchAssign = async () => {
    setBranchModalOpen(true);
    await fetchBranches();
    setSelectedBranches(survey?.assigned_branches || []);
  };

  const handleAssignBranches = async () => {
    setAssigningBranches(true);
    try {
      const res = await apiPost(`/surveys/builder/${surveyId}/assign-branches`, { branch_ids: selectedBranches });
      if (res.ok) {
        const data = await res.json();
        setSurvey(prev => ({ ...prev, assigned_branches: data.assigned_branches }));
        setQrCodes(data.qr_codes || []);
        setBranchModalOpen(false);
        toast.success(`Assigned ${selectedBranches.length} branches`);
      }
    } catch { toast.error('Failed to assign branches'); }
    finally { setAssigningBranches(false); }
  };

  // ─── RENDER HELPERS ───
  const selectedQuestion = questions.find(q => q.id === selectedQ);
  const voiceCount = questions.filter(q => q.type === 'voice').length;
  // Phase 4A types are not offered on Mystery Shopping surveys (unchanged this phase).
  const isMSSurvey = isMysteryShoppingSurvey(survey);
  const paletteItems = (group) => group.items.filter(i => !(i.keepDefaultText && isMSSurvey));
  const logicCount = questions.reduce((n, q) => n + (q.logic_rules?.length || 0), 0);

  // Plan-derived question count limit. `null` = unlimited (e.g. trust_circle,
  // platform_admin). Non-question structural blocks (section_header,
  // rich_text_block, image_display) don't count toward the cap.
  const NON_COUNTABLE_TYPES = new Set(['section_header', 'rich_text_block', 'image_display']);
  const questionLimit = planCaps?.question_limit ?? null;
  const currentQuestionCount = questions.filter(q => !NON_COUNTABLE_TYPES.has(q.type)).length;
  const limitReached = questionLimit != null && currentQuestionCount >= questionLimit;
  const canAddQuestion = !limitReached;
  const addQuestionDisabledMsg = questionLimit != null
    ? `You have reached your plan's question limit (${questionLimit} questions). Upgrade your plan to add more.`
    : '';

  // Settings helpers
  const updateSurveySetting = async (patch) => {
    try {
      const res = await apiPut(`/surveys/builder/${surveyId}`, patch);
      if (res.ok) {
        const updated = await res.json();
        setSurvey(updated);
        toast.success('Settings saved');
        return updated;
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save settings');
      }
    } catch { toast.error('Failed to save settings'); }
    return null;
  };

  const addQualifier = async (label) => {
    if (!label.trim()) return;
    try {
      const res = await apiPost(`/surveys/builder/${surveyId}/qualifiers`, { label });
      if (res.ok) {
        const data = await res.json();
        setSurvey(prev => ({ ...prev, qualifiers: data.qualifiers }));
        toast.success('Qualifier added');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add qualifier');
      }
    } catch { toast.error('Failed to add qualifier'); }
  };

  const renameQualifier = async (qualifierId, label) => {
    if (!label.trim()) return;
    try {
      const res = await apiPut(`/surveys/builder/${surveyId}/qualifiers/${qualifierId}`, { label });
      if (res.ok) {
        const data = await res.json();
        setSurvey(prev => ({ ...prev, qualifiers: data.qualifiers }));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to rename qualifier');
      }
    } catch { toast.error('Failed to rename qualifier'); }
  };

  const removeQualifier = async (qualifierId) => {
    try {
      const res = await apiDelete(`/surveys/builder/${surveyId}/qualifiers/${qualifierId}`);
      if (res.ok) {
        const data = await res.json();
        setSurvey(prev => ({ ...prev, qualifiers: data.qualifiers }));
        toast.success('Qualifier removed');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete qualifier');
      }
    } catch { toast.error('Failed to delete qualifier'); }
  };

  const toggleBranchAssignment = async (branchId, checked) => {
    const current = survey?.assigned_branches || [];
    const next = checked ? Array.from(new Set([...current, branchId])) : current.filter(id => id !== branchId);
    try {
      const res = await apiPost(`/surveys/builder/${surveyId}/assign-branches`, { branch_ids: next });
      if (res.ok) {
        const data = await res.json();
        setSurvey(prev => ({ ...prev, assigned_branches: data.assigned_branches }));
      } else if (next.length === 0) {
        // backend rejects empty list; update only via PUT to allow clearing
        const put = await apiPut(`/surveys/builder/${surveyId}`, { assigned_branches: [] });
        if (put.ok) {
          const d = await put.json();
          setSurvey(d);
        }
      } else {
        toast.error('Failed to update branches');
      }
    } catch { toast.error('Failed to update branches'); }
  };

  const skipCount = survey?.skip_rules?.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1A3E6F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5] flex flex-col" data-testid="survey-builder">

      {/* ─── TOP BAR ─── */}
      <div className="bg-[#1A3E6F] text-white px-4 py-3 flex items-center justify-between shrink-0 z-50" data-testid="builder-topbar">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLeaveDialogOpen(true)}
            className="flex items-center gap-1.5 text-white/90 hover:text-white hover:bg-white/10 px-2 py-1 rounded transition-colors"
            data-testid="back-to-dashboard-btn"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs font-medium hidden sm:inline">Back to Dashboard</span>
          </button>
          <div className="w-px h-6 bg-white/20" />
          <button onClick={() => setLeftOpen(!leftOpen)} className="p-1.5 rounded hover:bg-white/10" data-testid="toggle-left-panel">
            <Menu className="w-5 h-5" />
          </button>
          <div>
            <input
              ref={nameInputRef}
              className="bg-transparent text-white font-bold text-lg border-b border-transparent hover:border-white/30 focus:border-white focus:outline-none px-1 py-0.5 min-w-[200px]"
              defaultValue={survey?.name || ''}
              onBlur={(e) => { if (e.target.value !== survey?.name) updateSurveyName(e.target.value); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              data-testid="survey-name-input"
            />
            <p className="text-white/50 text-xs mt-0.5 px-1" data-testid="survey-question-count">
              {(survey?.product_type === 'nps' ? 'Net Promoter\u00AE Score' : survey?.product_type?.toUpperCase() || survey?.survey_type?.toUpperCase())} &middot; {survey?.assigned_branches?.length || 0} branches &middot; {currentQuestionCount}{questionLimit != null ? ` of ${questionLimit}` : ''} questions used
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => setSettingsOpen(true)} data-testid="survey-settings-btn">
            <SettingsIcon className="w-4 h-4 mr-1.5" /> Settings
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => setPreviewOpen(true)} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-1.5" /> Preview
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={handleDuplicate} data-testid="duplicate-btn">
            <Copy className="w-4 h-4 mr-1.5" /> Duplicate
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => navigate(`/surveys/${surveyId}/qualifiers`)} data-testid="tag-dimensions-btn">
            <Zap className="w-4 h-4 mr-1.5" /> Tag dimensions
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={openQrModal} data-testid="qr-btn">
            <QrCode className="w-4 h-4 mr-1.5" /> QR Codes
          </Button>
          {published ? (
            <Button size="sm" className="bg-white text-[#1A3E6F] hover:bg-gray-100" disabled data-testid="published-badge">
              <Check className="w-4 h-4 mr-1.5 text-emerald-600" /> Published
            </Button>
          ) : (() => {
            // Phase 1.5 — block Publish when active-survey allowance reached.
            const limit = planCaps?.active_survey_limit;
            const used = planCaps?.active_surveys_used ?? 0;
            const atLimit = typeof limit === 'number' && used >= limit;
            const title = atLimit
              ? `${used} of ${limit} active surveys used — deactivate one to activate another`
              : undefined;
            return (
              <Button
                size="sm"
                className="bg-white text-[#1A3E6F] hover:bg-gray-100"
                onClick={handlePublish}
                disabled={publishing || atLimit}
                title={title}
                data-testid="publish-btn"
              >
                <Send className="w-4 h-4 mr-1.5" /> {publishing ? 'Publishing...' : atLimit ? 'Publish (limit reached)' : 'Publish'}
              </Button>
            );
          })()}
        </div>
      </div>

      {/* ─── STATUS ROW ─── */}
      <div className="bg-[#F8F9FA] border-b border-[#E2E8F0] px-4 py-2 flex items-center justify-between text-xs text-[#64748B] shrink-0" data-testid="status-row">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${survey?.status === 'active' ? 'bg-emerald-500' : survey?.status === 'paused' ? 'bg-amber-500' : 'bg-blue-400'}`} />
            <span className="font-medium capitalize">{survey?.status}</span>
          </span>
          <span>{questions.length} questions</span>
          <span>{voiceCount} voice</span>
          <span>{logicCount} logic rules</span>
          <span>{skipCount} skip rules</span>
          {typeof planCaps?.active_survey_limit === 'number' && (
            <span
              className={
                (planCaps.active_surveys_used ?? 0) >= planCaps.active_survey_limit
                  ? 'text-rose-600 font-medium'
                  : ''
              }
              data-testid="active-survey-counter"
            >
              {planCaps.active_surveys_used ?? 0} of {planCaps.active_survey_limit} active surveys used
              {(planCaps.active_surveys_used ?? 0) >= planCaps.active_survey_limit && !published && (
                <span className="ml-1">— deactivate one to activate another</span>
              )}
            </span>
          )}
        </div>
        <span>{survey?.assigned_branches?.length || 0} assigned branches</span>
      </div>

      {/* ─── THREE PANEL BODY ─── */}
      <div className="flex-1 grid min-h-[580px]" style={{ gridTemplateColumns: `${leftOpen ? '200px' : '0px'} 1fr 260px` }}>

        {/* LEFT PANEL: Question Library */}
        <div className={`bg-[#FAFBFC] border-r border-[#E2E8F0] overflow-y-auto transition-all ${leftOpen ? 'p-3' : 'p-0 overflow-hidden'}`} data-testid="question-library">
          {leftOpen && (
            <>
              <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider mb-3">Question Types</p>
              {QUESTION_TYPES.map((group) => (
                <div key={group.group} className="mb-4">
                  <p className="text-[10px] font-semibold text-[#94A3B8] uppercase mb-1.5">{group.group}</p>
                  {paletteItems(group).map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.key || item.type}
                        draggable
                        onDragStart={(e) => onLibraryDragStart(e, item)}
                        onDragEnd={onCanvasDragEnd}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-grab hover:bg-[#E2E8F0]/60 text-[#334155] text-xs font-medium mb-0.5 select-none active:cursor-grabbing"
                        data-testid={`library-item-${item.key || item.type}`}
                      >
                        <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: item.color }} />
                        {item.label}
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </div>

        {/* CENTRE PANEL: Canvas */}
        <div
          className="bg-[#F0F2F5] overflow-y-auto p-4"
          data-testid="survey-canvas"
          onDragOver={(e) => {
            // Make the entire canvas a valid drop target for library items, including
            // the empty whitespace below the bottom zone — fixes the case where dropping
            // outside any card on a survey that already has questions silently fails.
            if (dragType === 'library') { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }
          }}
          onDrop={(e) => {
            // Catch-all drop. onCanvasDrop consumes drag state synchronously, so a
            // bubbled drop after a card-level / inner-wrapper drop is a safe no-op.
            if (dragType === 'library') onCanvasDrop(e, questions.length);
          }}
        >
          <p className="text-[10px] text-[#94A3B8] font-medium mb-3 uppercase tracking-wider">Drag questions to reorder</p>

          {questions.length === 0 ? (
            <div
              className="border-2 border-dashed border-[#CBD5E1] rounded-xl p-10 text-center"
              onDragOver={(e) => { e.preventDefault(); setDragOverIndex(0); }}
              onDrop={(e) => onCanvasDrop(e, 0)}
              onDragLeave={() => setDragOverIndex(null)}
            >
              <Layout className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-sm text-[#64748B] font-medium">Drag a question type here to start</p>
              <p className="text-xs text-[#94A3B8] mt-1">or use the buttons below</p>
            </div>
          ) : (
            <div
              className="space-y-2"
              onDragOver={(e) => {
                if (dragType === 'library') onCanvasDragOver(e, questions.length);
              }}
              onDrop={(e) => {
                // Catch drops that land in the gaps between cards or in the canvas
                // whitespace. onCanvasDrop consumes drag state synchronously, so
                // a bubbled drop after a card-level drop is a safe no-op.
                if (dragType === 'library') onCanvasDrop(e, questions.length);
              }}
              onDragLeave={() => setDragOverIndex(null)}
              data-testid="canvas-drop-zone"
            >
              {(() => {
                const numberMap = computeQuestionNumbers(questions);
                return questions.map((q, idx) => {
                const tc = getTypeConfig(q.type, q);
                const isSelected = selectedQ === q.id;
                const isNonQuestion = NON_QUESTION_TYPES.has(q.type);
                const displayNum = numberMap[q.id];
                const isSection = q.type === 'section_header';

                return (
                  <React.Fragment key={q.id}>
                    {/* Drop zone indicator */}
                    {dragOverIndex === idx && (
                      <div className="h-1 bg-[#3B82F6] rounded-full mx-2 transition-all" />
                    )}
                    <div
                      draggable
                      onDragStart={(e) => onReorderDragStart(e, idx)}
                      onDragOver={(e) => onCanvasDragOver(e, idx)}
                      onDrop={(e) => onCanvasDrop(e, idx)}
                      onDragEnd={onCanvasDragEnd}
                      onClick={() => setSelectedQ(q.id)}
                      className={`rounded-lg border-2 transition-all cursor-pointer group ${
                        isSelected ? 'border-[#1A3E6F] shadow-md' : 'border-[#E2E8F0] hover:border-[#1A3E6F]/30'
                      } ${isNonQuestion ? 'bg-[#F8FAFC] border-dashed' : 'bg-white'}`}
                      data-testid={`question-card-${q.id}`}
                    >
                      <div className="flex items-center gap-2 px-3 py-3">
                        <GripVertical className="w-4 h-4 text-[#CBD5E1] cursor-grab shrink-0 active:cursor-grabbing" />
                        {displayNum == null ? (
                          <span className="w-6 h-6 rounded bg-[#E2E8F0] flex items-center justify-center shrink-0" aria-hidden="true">
                            <Layout className="w-3 h-3 text-[#64748B]" />
                          </span>
                        ) : isSection ? (
                          <span className="min-w-[24px] h-6 px-1.5 rounded bg-[#1A3E6F] text-white flex items-center justify-center text-[11px] font-bold shrink-0" data-testid={`q-num-${q.id}`}>{displayNum}</span>
                        ) : (
                          <span className="min-w-[24px] h-6 px-1 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[10px] font-bold text-[#64748B] shrink-0" data-testid={`q-num-${q.id}`}>{displayNum}</span>
                        )}
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white shrink-0" style={{ backgroundColor: tc.color }} data-testid={`q-badge-${q.id}`}>{tc.label}</span>
                        {q.condition && q.condition.question_id && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 flex items-center gap-0.5 shrink-0"
                            data-testid={`q-conditional-${q.id}`}
                            title="This question is shown only when a previous answer matches"
                          >
                            <GitBranch className="w-3 h-3" /> Conditional
                          </span>
                        )}
                        <span className={`text-sm flex-1 truncate ${isNonQuestion ? 'text-[#64748B] italic' : 'text-[#0F172A] font-medium'}`} data-testid={`q-text-${q.id}`}>
                          {(stripHtml(q.text) || (q.type === 'rich_text_block' ? 'Formatted content block' : q.type === 'image_display' ? 'Image block' : '')).slice(0, 60)}
                          {stripHtml(q.text).length > 60 ? '\u2026' : ''}
                          {q.is_required && !isNonQuestion ? <span className="text-red-500 ml-1" data-testid={`q-required-${q.id}`}>*</span> : null}
                        </span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button onClick={(e) => { e.stopPropagation(); setSelectedQ(q.id); }} className="p-1 rounded hover:bg-[#F1F5F9]" data-testid={`edit-q-${q.id}`} aria-label="Edit question">
                            <Pencil className="w-3.5 h-3.5 text-[#64748B]" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); duplicateQuestion(q); }} className="p-1 rounded hover:bg-[#F1F5F9]" data-testid={`dup-q-${q.id}`} aria-label="Duplicate question">
                            <Copy className="w-3.5 h-3.5 text-[#64748B]" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteQId(q.id); }} className="p-1 rounded hover:bg-red-50" data-testid={`del-q-${q.id}`} aria-label="Delete question">
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        </div>
                      </div>
                      {/* Platform-admin only: surface the question's DB id
                          for debugging / audit-trail tracing. Renders nothing
                          for other roles. */}
                      <div className="px-3 pb-1 -mt-1">
                        <AdminIdRow questionId={q.id} testId={`builder-qid-${q.id}`} />
                      </div>
                      {/* Logic/Skip tags */}
                      {(q.logic_rules?.length > 0 || q.type === 'section_header') && (
                        <div className="px-3 pb-2 flex gap-1.5">
                          {q.logic_rules?.length > 0 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium flex items-center gap-1">
                              <Zap className="w-3 h-3" /> {q.logic_rules.length} logic rule{q.logic_rules.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              });
              })()}
              {/* Drop zone at end */}
              {dragOverIndex === questions.length && (
                <div className="h-1 bg-[#3B82F6] rounded-full mx-2" />
              )}
              <div
                className="h-10"
                onDragOver={(e) => onCanvasDragOver(e, questions.length)}
                onDrop={(e) => onCanvasDrop(e, questions.length)}
                onDragLeave={() => setDragOverIndex(null)}
              />
            </div>
          )}

          {/* Bottom buttons */}
          <div className="flex gap-2 mt-4 items-center">
            <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)} disabled={!canAddQuestion} data-testid="add-question-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Question
            </Button>
            <Button variant="outline" size="sm" onClick={() => addQuestion('section_header', { text: 'New Section' })} disabled={!canAddQuestion} data-testid="add-section-btn">
              <Layout className="w-4 h-4 mr-1" /> Add Section
            </Button>
          </div>
          {!canAddQuestion && (
            <p className="text-xs text-[#D62B27] mt-2 flex items-center gap-1.5" data-testid="max-questions-msg">
              <AlertCircle className="w-3.5 h-3.5" /> {addQuestionDisabledMsg}
            </p>
          )}
        </div>

        {/* RIGHT PANEL: Question Settings */}
        <div className="bg-white border-l border-[#E2E8F0] overflow-y-auto p-4" data-testid="question-settings">
          {!selectedQuestion ? (
            <div className="text-center py-16">
              <Pencil className="w-8 h-8 text-[#CBD5E1] mx-auto mb-3" />
              <p className="text-sm text-[#94A3B8]">Select a question to edit its settings</p>
            </div>
          ) : (
            <QuestionSettings
              question={selectedQuestion}
              questions={questions}
              survey={survey}
              surveyId={surveyId}
              gapCategories={gapCategories}
              onUpdate={updateQuestion}
              onSurveyUpdate={(updates) => { apiPut(`/surveys/builder/${surveyId}`, updates).then(() => fetchSurvey()); }}
            />
          )}
        </div>
      </div>

      {/* ─── PREVIEW MODAL ─── */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-testid="preview-modal">
          <div className="bg-[#1A3E6F] text-white px-4 py-3 flex items-center justify-between gap-2">
            <span className="font-semibold text-sm">Survey Preview</span>
            <span className="text-[11px] px-2 py-0.5 rounded bg-white/15" data-testid="preview-experience-badge">
              {experienceLabel(resolveExperience(survey))}
            </span>
            <a href={`/survey-builder/${surveyId}/preview`} target="_blank" rel="noreferrer" className="ml-auto text-[11px] underline hover:text-white/80" data-testid="preview-open-new-tab">
              Open full screen
            </a>
            <button onClick={() => setPreviewOpen(false)} className="p-1 rounded hover:bg-white/10" data-testid="preview-close-btn"><X className="w-4 h-4" /></button>
          </div>
          <iframe
            src={`/survey-builder/${surveyId}/preview`}
            title="Survey preview"
            className="w-full h-[70vh] border-0 bg-[#F0F2F5]"
            data-testid="preview-iframe"
          />
        </DialogContent>
      </Dialog>

      {/* ─── QR CODES MODAL ─── */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="max-w-lg" data-testid="qr-modal">
          <DialogHeader>
            <DialogTitle>QR Codes</DialogTitle>
          </DialogHeader>
          <QrCodesPanel
            qrCodes={qrCodes}
            onAssign={openBranchAssign}
            surveyId={surveyId}
            allowPublicPreview={!!survey?.allow_public_preview}
          />
        </DialogContent>
      </Dialog>

      {/* ─── BRANCH ASSIGN MODAL ─── */}
      <Dialog open={branchModalOpen} onOpenChange={setBranchModalOpen}>
        <DialogContent className="max-w-md" data-testid="branch-assign-modal">
          <DialogHeader>
            <DialogTitle>Assign Branches</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input
                value={branchSearch}
                onChange={(e) => setBranchSearch(e.target.value)}
                placeholder="Search branches..."
                className="pl-9"
                data-testid="branch-search"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-1">
              {allBranches
                .filter(b => b.name?.toLowerCase().includes(branchSearch.toLowerCase()))
                .map(b => (
                  <label key={b.branch_id} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F8F9FA] cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selectedBranches.includes(b.branch_id)}
                      onChange={(e) => {
                        setSelectedBranches(prev =>
                          e.target.checked ? [...prev, b.branch_id] : prev.filter(id => id !== b.branch_id)
                        );
                      }}
                      className="rounded border-[#CBD5E1]"
                    />
                    <span className="text-[#0F172A]">{b.name}</span>
                  </label>
                ))}
            </div>
            <Button onClick={handleAssignBranches} disabled={assigningBranches || selectedBranches.length === 0} className="w-full bg-[#1A3E6F]" data-testid="confirm-assign-btn">
              {assigningBranches ? 'Assigning...' : `Assign ${selectedBranches.length} Branch${selectedBranches.length !== 1 ? 'es' : ''}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── LEAVE BUILDER DIALOG ─── */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent className="max-w-md" data-testid="leave-builder-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Montserrat'] text-[#0F172A]">Leave Survey Builder?</DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Your changes are saved automatically. You can return and keep editing at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setLeaveDialogOpen(false)}
              data-testid="leave-dialog-stay-btn"
            >
              Stay
            </Button>
            <Button
              className="bg-[#1A3E6F] hover:bg-[#15325A] text-white"
              onClick={() => {
                setLeaveDialogOpen(false);
                navigate(user?.role === 'platform_admin' ? '/platform' : '/dashboard');
              }}
              data-testid="leave-dialog-leave-btn"
            >
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── DELETE QUESTION CONFIRM DIALOG ─── */}
      <Dialog open={!!deleteQId} onOpenChange={(open) => { if (!open) setDeleteQId(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-question-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Montserrat'] text-[#0F172A]">Delete this question?</DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Are you sure you want to delete this question? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDeleteQId(null)} data-testid="delete-question-cancel">Cancel</Button>
            <Button
              className="bg-[#D62B27] hover:bg-[#B51F1C] text-white"
              onClick={async () => {
                const id = deleteQId;
                setDeleteQId(null);
                if (id) await deleteQuestion(id);
              }}
              data-testid="delete-question-confirm"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── SURVEY SETTINGS DIALOG ─── */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="survey-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
              <SettingsIcon className="w-4 h-4 text-[#1A3E6F]" /> Survey Settings
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Control product type, branch assignment, scoring rules, qualifiers, and footer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Survey Name */}
            <div>
              <label className="text-xs font-semibold text-[#334155] block mb-1.5">Survey Name <span className="text-red-500">*</span></label>
              <Input
                defaultValue={survey?.name || ''}
                onBlur={(e) => {
                  if (e.target.value.trim() && e.target.value !== survey?.name) updateSurveyName(e.target.value.trim());
                }}
                data-testid="settings-name-input"
              />
              <p className="text-[11px] text-[#94A3B8] mt-1">Required before publishing.</p>
            </div>

            {/* Product Type */}
            <div>
              <label className="text-xs font-semibold text-[#334155] block mb-1.5">Product Type</label>
              <Select
                value={survey?.product_type || 'vox'}
                onValueChange={(v) => updateSurveySetting({ product_type: v })}
                disabled={!planCaps || (planCaps.product_types?.length || 0) <= 1}
              >
                <SelectTrigger data-testid="settings-product-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {planCaps?.product_types?.includes('nps') && (
                    <SelectItem value="nps" data-testid="product-nps">NPS&reg; &mdash; Net Promoter&reg; Score</SelectItem>
                  )}
                  {planCaps?.product_types?.includes('csat') && (
                    <SelectItem value="csat" data-testid="product-csat">CSAT</SelectItem>
                  )}
                  {planCaps?.product_types?.includes('vox') && (
                    <SelectItem value="vox" data-testid="product-vox">VOX</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#94A3B8] mt-1">Current: {currentQuestionCount}{questionLimit != null ? ` of ${questionLimit}` : ''} questions used.</p>
            </div>

            <SurveyExperienceSetting
              survey={survey}
              onChange={(v) => updateSurveySetting({ presentation_version: v })}
            />

            <SurveyAccessSetting
              survey={survey}
              branches={allBranches}
              onChange={(v) => updateSurveySetting({ access_validation: v })}
            />

            <PublicEntrySetting
              survey={survey}
              onSave={(v) => updateSurveySetting({ public_entry: v })}
            />

            {/* Branch Assignment */}
            <div>
              <label className="text-xs font-semibold text-[#334155] block mb-1.5">Branch Assignment <span className="text-red-500">*</span></label>
              {allBranches.length === 0 ? (
                <p className="text-xs text-[#D62B27] py-2" data-testid="no-branches-msg">No branches found. Create branches first.</p>
              ) : (
                <div className="max-h-[180px] overflow-y-auto border border-[#E2E8F0] rounded-md divide-y divide-[#F1F5F9]" data-testid="settings-branch-list">
                  {allBranches.map(b => (
                    <label key={b.branch_id} className="flex items-center gap-2 px-3 py-2 hover:bg-[#F8F9FA] cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={(survey?.assigned_branches || []).includes(b.branch_id)}
                        onChange={(e) => toggleBranchAssignment(b.branch_id, e.target.checked)}
                        className="rounded border-[#CBD5E1]"
                        data-testid={`settings-branch-${b.branch_id}`}
                      />
                      <span className="text-[#0F172A]">{b.name}</span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-[#94A3B8] mt-1">At least one branch required before publishing.</p>
            </div>

            {/* Top Box Rule */}
            <div>
              <label className="text-xs font-semibold text-[#334155] block mb-1.5">Top Box Rule (CSAT)</label>
              <Select
                value={survey?.top_box_rule || 'top_1'}
                onValueChange={(v) => updateSurveySetting({ top_box_rule: v })}
              >
                <SelectTrigger data-testid="settings-top-box"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="top_1" data-testid="top-1">Top 1 box &mdash; only score 5 counts as satisfied</SelectItem>
                  <SelectItem value="top_2" data-testid="top-2">Top 2 box &mdash; scores 4 and 5 count as satisfied</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* TrustX Qualifiers */}
            <div>
              <label className="text-xs font-semibold text-[#334155] flex items-center gap-1.5 mb-1.5">
                <Shield className="w-3.5 h-3.5" /> TrustX&reg; Qualifiers
              </label>
              <div className="space-y-2" data-testid="settings-qualifiers-list">
                {(survey?.qualifiers || []).map((q) => (
                  <div key={q.id} className="flex items-center gap-2 border border-[#E2E8F0] rounded-md px-2 py-1.5">
                    <Tag className="w-3.5 h-3.5 text-[#64748B]" />
                    <Input
                      defaultValue={q.label}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== q.label) renameQualifier(q.id, v);
                      }}
                      className="h-7 text-xs border-none focus-visible:ring-0 px-1"
                      data-testid={`qualifier-input-${q.id}`}
                    />
                    <button
                      onClick={() => removeQualifier(q.id)}
                      disabled={(survey?.qualifiers || []).length <= 1}
                      className="p-1 rounded hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`qualifier-del-${q.id}`}
                      aria-label="Delete qualifier"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 text-xs"
                onClick={() => {
                  const label = window.prompt('New qualifier name:');
                  if (label && label.trim()) addQualifier(label.trim());
                }}
                data-testid="add-qualifier-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Qualifier
              </Button>
            </div>

            {/* Comparison Survey (platform_admin only) — when ON, the
                report header + QA review render a side-by-side header for
                the two restaurants captured by the chosen questions. Zero
                effect on non-comparison surveys; flag is false by default. */}
            {user?.role === 'platform_admin' && (
              <div className="space-y-3 border border-dashed border-[#CBD5E1] rounded-md p-3 bg-[#F8FAFC]" data-testid="settings-comparison-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#0F172A] font-semibold flex items-center gap-1.5">
                      Comparison survey
                      <span className="text-[10px] uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] font-bold px-1.5 py-0.5 rounded">PLATFORM</span>
                    </p>
                    <p className="text-[11px] text-[#94A3B8]">When on, reports render a side-by-side header for two restaurants.</p>
                  </div>
                  <Switch
                    checked={!!survey?.is_comparison}
                    onCheckedChange={(v) => updateSurveySetting({
                      is_comparison: v,
                      // Initialize the picker object so subsequent edits hit
                      // a stable shape; clears values when turning the flag
                      // off so a future re-enable starts fresh.
                      comparison_question_ids: v
                        ? (survey?.comparison_question_ids || { r1_brand: null, r1_branch: null, r2_brand: null, r2_branch: null })
                        : { r1_brand: null, r1_branch: null, r2_brand: null, r2_branch: null },
                    })}
                    data-testid="settings-comparison-toggle"
                  />
                </div>

                {survey?.is_comparison && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1" data-testid="settings-comparison-pickers">
                    {[
                      { key: 'r1_brand',  label: 'Restaurant 1 — Brand/Name' },
                      { key: 'r1_branch', label: 'Restaurant 1 — Branch' },
                      { key: 'r2_brand',  label: 'Restaurant 2 — Brand/Name' },
                      { key: 'r2_branch', label: 'Restaurant 2 — Branch' },
                    ].map(({ key, label }) => {
                      const currentValue = survey?.comparison_question_ids?.[key] || 'unset';
                      // Pickable = real questions only (drop layout/non-input types).
                      const pickable = (questions || []).filter(q => !['section_header','rich_text','page_break','welcome','thank_you','image_display','confirmation'].includes(q.question_type || q.type));
                      return (
                        <div key={key} className="space-y-1">
                          <label className="text-[11px] font-semibold text-[#334155] block">{label}</label>
                          <Select
                            value={currentValue}
                            onValueChange={(v) => updateSurveySetting({
                              comparison_question_ids: {
                                ...(survey?.comparison_question_ids || {}),
                                [key]: v === 'unset' ? null : v,
                              },
                            })}
                          >
                            <SelectTrigger data-testid={`settings-comparison-picker-${key}`} className="text-xs">
                              <SelectValue placeholder="Pick a question…" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[280px]">
                              <SelectItem value="unset" className="text-xs italic text-[#94A3B8]">Not set</SelectItem>
                              {pickable.map((q, idx) => {
                                const txt = cleanQuestionLabel(q.question_text || q.label || '').slice(0, 60);
                                return (
                                  <SelectItem key={q.id} value={q.id} className="text-xs">
                                    {idx + 1}. {txt || <em>(untitled)</em>}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Footer Settings */}
            <div className="space-y-3">
              <label className="text-xs font-semibold text-[#334155] block">Survey Footer</label>
              <div className="flex items-center justify-between border border-[#E2E8F0] rounded-md px-3 py-2">
                <div>
                  <p className="text-sm text-[#0F172A]">Show &ldquo;Powered by satisFIND&reg;&rdquo;</p>
                  {planCaps?.plan_type === 'free_nps' && (
                    <p className="text-[11px] text-[#94A3B8]">Required on NPS&reg; Free plan &mdash; upgrade to remove.</p>
                  )}
                </div>
                <Switch
                  checked={survey?.footer_settings?.show_powered_by !== false}
                  onCheckedChange={(v) => updateSurveySetting({ footer_settings: { ...(survey?.footer_settings || {}), show_powered_by: v } })}
                  disabled={planCaps?.plan_type === 'free_nps'}
                  data-testid="settings-powered-by"
                />
              </div>
              <div className="flex items-center justify-between border border-[#E2E8F0] rounded-md px-3 py-2">
                <div>
                  <p className="text-sm text-[#0F172A]">Show consent section</p>
                  <p className="text-[11px] text-[#94A3B8]">Hide the &ldquo;I agree…&rdquo; checkbox on contact info questions.</p>
                </div>
                <Switch
                  checked={survey?.show_consent !== false}
                  onCheckedChange={(v) => updateSurveySetting({ show_consent: v })}
                  data-testid="settings-show-consent"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#0F172A]">Group questions by section</p>
                  <p className="text-[11px] text-[#94A3B8]">Render every question under a section header on the same scrollable page. Default: one-question-per-page.</p>
                </div>
                <Switch
                  checked={!!survey?.section_grouped_display}
                  onCheckedChange={(v) => updateSurveySetting({ section_grouped_display: v })}
                  data-testid="settings-section-grouped"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#0F172A]">Allow public preview</p>
                  <p className="text-[11px] text-[#94A3B8]">Enable a read-only preview at <span className="font-mono">…/preview</span> so mystery shoppers can review the full survey before submitting. Score values stay hidden.</p>
                </div>
                <Switch
                  checked={!!survey?.allow_public_preview}
                  onCheckedChange={(v) => updateSurveySetting({ allow_public_preview: v })}
                  data-testid="settings-allow-preview"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#0F172A]">Send confirmation email to respondent</p>
                  <p className="text-[11px] text-[#94A3B8]">On submission, email a branded confirmation to the respondent&rsquo;s email answer, with all tenant admins on CC. Requires an Email question in this survey.</p>
                </div>
                <Switch
                  checked={!!survey?.send_confirmation_email}
                  onCheckedChange={(v) => updateSurveySetting({ send_confirmation_email: v })}
                  data-testid="settings-send-confirmation-email"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#64748B]">Privacy Policy URL</label>
                <Input
                  defaultValue={survey?.footer_settings?.privacy_policy_url || ''}
                  placeholder="https://example.com/privacy"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (survey?.footer_settings?.privacy_policy_url || '')) {
                      updateSurveySetting({ footer_settings: { ...(survey?.footer_settings || {}), privacy_policy_url: v } });
                    }
                  }}
                  data-testid="settings-privacy-url"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#64748B]">Custom Footer Text</label>
                <Input
                  defaultValue={survey?.footer_settings?.custom_footer_text || ''}
                  placeholder="Shown at the bottom of every survey page"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (survey?.footer_settings?.custom_footer_text || '')) {
                      updateSurveySetting({ footer_settings: { ...(survey?.footer_settings || {}), custom_footer_text: v } });
                    }
                  }}
                  data-testid="settings-custom-footer"
                />
              </div>
              {/* NPS® trademark notice toggle. Defaults: ON if any NPS question
                  exists, OFF otherwise. Tenant can override either way. */}
              {(() => {
                const hasNpsQuestion = questions.some(q => q.type === 'nps');
                const stored = survey?.show_nps_trademark_footer;
                const checked = stored === undefined || stored === null ? hasNpsQuestion : !!stored;
                return (
                  <div className="flex items-center justify-between border border-[#E2E8F0] rounded-md px-3 py-2">
                    <div>
                      <p className="text-sm text-[#0F172A]">Show Net Promoter&reg; Score trademark notice</p>
                      <p className="text-[11px] text-[#94A3B8]">
                        Default: ON when this survey contains an NPS&reg; question.{' '}
                        {hasNpsQuestion ? 'Currently has an NPS\u00AE question.' : 'No NPS\u00AE question detected.'}
                      </p>
                    </div>
                    <Switch
                      checked={checked}
                      onCheckedChange={(v) => updateSurveySetting({ show_nps_trademark_footer: !!v })}
                      data-testid="settings-nps-trademark"
                    />
                  </div>
                );
              })()}
            </div>

            {/* Survey Status */}
            <div>
              <label className="text-xs font-semibold text-[#334155] block mb-1.5">Survey Status</label>
              <Select
                value={survey?.status || 'draft'}
                onValueChange={(v) => updateSurveySetting({ status: v })}
              >
                <SelectTrigger data-testid="settings-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft" data-testid="status-draft">Draft</SelectItem>
                  <SelectItem value="active" data-testid="status-active">Active</SelectItem>
                  <SelectItem value="paused" data-testid="status-paused">Paused</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#94A3B8] mt-1">Only Active surveys accept responses. Requires name, at least one question, and at least one branch assigned.</p>
            </div>

            {/* Archive Survey */}
            {survey?.status !== 'archived' && (
              <div className="border-t border-[#E2E8F0] pt-4">
                <label className="text-xs font-semibold text-[#334155] block mb-1.5">Archive Survey</label>
                <p className="text-[11px] text-[#94A3B8] mb-2">Archived surveys no longer accept responses but all existing data is preserved.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setArchiveDialogOpen(true)}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="archive-survey-btn"
                >
                  <Archive className="w-4 h-4 mr-1.5" /> Archive this survey
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setSettingsOpen(false)} className="bg-[#1A3E6F] hover:bg-[#15325A] text-white" data-testid="settings-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent data-testid="archive-confirm-dialog">
          <DialogHeader><DialogTitle>Archive this survey?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B]">
            Archive this survey? It will no longer accept responses but all existing data is preserved. You can contact satisFIND to restore it.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialogOpen(false)} disabled={archiving} data-testid="archive-cancel-btn">Cancel</Button>
            <Button onClick={handleArchive} disabled={archiving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="archive-confirm-btn">
              {archiving ? 'Archiving…' : 'Archive Survey'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" data-testid="question-picker-dialog">
          <DialogHeader><DialogTitle>Add a question</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] -mt-2">Pick a question type to add to your survey.</p>
          {limitReached && (
            <div className="mt-3 px-3 py-2.5 rounded-lg bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] text-sm flex items-start gap-2" data-testid="picker-limit-reached">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{addQuestionDisabledMsg}</span>
            </div>
          )}
          <div className="space-y-4 mt-2">
            {QUESTION_TYPES.map((group) => (
              <div key={group.group}>
                <p className="text-[10px] font-semibold text-[#94A3B8] uppercase mb-2 tracking-wider">{group.group}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {paletteItems(group).map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key || item.type}
                        type="button"
                        disabled={limitReached}
                        onClick={async () => { setPickerOpen(false); await addQuestion(item.type, { ...item.defaults, text: item.keepDefaultText ? item.defaults.text : item.label }); }}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#E2E8F0] hover:bg-[#F8FAFC] hover:border-[#CBD5E1] text-left text-sm font-medium text-[#334155] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-[#E2E8F0]"
                        data-testid={`picker-item-${item.key || item.type}`}
                      >
                        <Icon className="w-4 h-4 shrink-0" style={{ color: item.color }} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)} data-testid="picker-cancel-btn">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── QUESTION SETTINGS COMPONENT ───
// ─── RICH TEXT EDITOR (whitelist: bold/italic/underline + lists) ───
// Author-only editor. Toolbar is intentionally limited to the tags the
// backend/frontend sanitizer preserves (see lib/sanitize.js). Anything
// pasted from Word/browser is scrubbed on paste + again on save.
function RichTextEditor({ value, onChange, placeholder, testId, rows = 2 }) {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => {
    // Always emit sanitised HTML so downstream state + save-time
    // sanitizer see the same shape a respondent will render.
    onChange(sanitizeHtml(ref.current?.innerHTML || '') || '');
  };

  const exec = (cmd, val = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    emit();
  };

  const handlePaste = (e) => {
    // Intercept clipboard → strip Word/browser HTML → insert clean HTML.
    e.preventDefault();
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    const html = cd.getData('text/html');
    const text = cd.getData('text/plain');
    const cleanHtml = html
      ? sanitizeHtml(html)
      : (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\r?\n/g, '<br>');
    // insertHTML respects the caret + current selection; falls back to
    // plain-text insertion if the browser refuses HTML insertion.
    try {
      document.execCommand('insertHTML', false, cleanHtml || '');
    } catch (_) {
      document.execCommand('insertText', false, text || '');
    }
    emit();
  };

  return (
    <div className="border border-[#E2E8F0] rounded-md overflow-hidden bg-white" data-testid={testId}>
      <div className="flex items-center gap-0.5 bg-[#F8FAFC] border-b border-[#E2E8F0] px-1 py-1">
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} className="p-1 rounded hover:bg-[#E2E8F0]" title="Bold" data-testid={`${testId}-bold`}><Bold className="w-3.5 h-3.5" /></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} className="p-1 rounded hover:bg-[#E2E8F0]" title="Italic" data-testid={`${testId}-italic`}><Italic className="w-3.5 h-3.5" /></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('underline'); }} className="p-1 rounded hover:bg-[#E2E8F0]" title="Underline" data-testid={`${testId}-underline`}><Underline className="w-3.5 h-3.5" /></button>
        <div className="w-px h-4 bg-[#E2E8F0] mx-1" />
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} className="p-1 rounded hover:bg-[#E2E8F0]" title="Bulleted list" data-testid={`${testId}-bullet-list`}><ListIcon className="w-3.5 h-3.5" /></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }} className="p-1 rounded hover:bg-[#E2E8F0]" title="Numbered list" data-testid={`${testId}-ordered-list`}><ListOrdered className="w-3.5 h-3.5" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onPaste={handlePaste}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="px-2 py-1.5 text-sm outline-none min-h-[2.5rem]"
        style={{ minHeight: `${rows * 1.5}rem` }}
        data-testid={`${testId}-input`}
      />
      {!focused && !value && placeholder && (
        <div className="absolute text-xs text-[#94A3B8] pointer-events-none px-2 py-1.5" style={{ marginTop: `-${rows * 1.5}rem` }}>{placeholder}</div>
      )}
    </div>
  );
}

// ─── CHOICE EDITOR (Single/Multi/Button/Dropdown) ───
function ChoiceEditor({ choices, onChange, withScore = true, minChoices = 2, testId = 'choices' }) {
  const list = Array.isArray(choices) ? choices.map(c => typeof c === 'string' ? { label: c, score_value: null } : c) : [];
  const [draftLabels, setDraftLabels] = useState(list.map(c => c.label));
  const [draftScores, setDraftScores] = useState(list.map(c => c.score_value == null ? '' : String(c.score_value)));

  useEffect(() => {
    setDraftLabels(list.map(c => c.label));
    setDraftScores(list.map(c => c.score_value == null ? '' : String(c.score_value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(choices)]);

  const commit = (newList) => onChange(newList);

  const updateLabel = (i, val) => {
    const newLabels = [...draftLabels]; newLabels[i] = val; setDraftLabels(newLabels);
  };
  const blurLabel = (i) => {
    const newList = list.map((c, idx) => idx === i ? { ...c, label: draftLabels[i] } : c);
    if (newList[i].label !== list[i].label) commit(newList);
  };
  const updateScore = (i, val) => {
    const newScores = [...draftScores]; newScores[i] = val; setDraftScores(newScores);
  };
  const blurScore = (i) => {
    const raw = draftScores[i];
    const parsed = raw === '' || raw == null ? null : Number(raw);
    const newList = list.map((c, idx) => idx === i ? { ...c, score_value: isNaN(parsed) ? null : parsed } : c);
    if ((newList[i].score_value ?? '') !== (list[i].score_value ?? '')) commit(newList);
  };
  const removeAt = (i) => { if (list.length <= minChoices) return; commit(list.filter((_, idx) => idx !== i)); };
  const add = () => commit([...list, { label: `Option ${list.length + 1}`, score_value: null }]);
  const move = (from, to) => {
    if (to < 0 || to >= list.length) return;
    const copy = [...list];
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    commit(copy);
  };

  return (
    <div className="space-y-2 border-t border-[#E2E8F0] pt-4" data-testid={testId}>
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Choices</h4>
      {list.map((c, i) => (
        <div key={i} className="flex items-center gap-1.5 bg-[#F8FAFC] rounded-md px-1.5 py-1 border border-[#E2E8F0]">
          <GripVertical className="w-3.5 h-3.5 text-[#CBD5E1] cursor-grab" />
          <Input
            value={draftLabels[i] ?? ''}
            onChange={(e) => updateLabel(i, e.target.value)}
            onBlur={() => blurLabel(i)}
            className="h-7 text-xs flex-1 border-0 bg-transparent focus-visible:ring-0"
            data-testid={`${testId}-label-${i}`}
          />
          {withScore && (
            <Input
              value={draftScores[i] ?? ''}
              onChange={(e) => updateScore(i, e.target.value)}
              onBlur={() => blurScore(i)}
              placeholder="Score"
              className="h-7 text-xs w-14 text-center"
              data-testid={`${testId}-score-${i}`}
            />
          )}
          <button onClick={() => move(i, i - 1)} disabled={i === 0} className="p-0.5 disabled:opacity-30" aria-label="Move up" data-testid={`${testId}-up-${i}`}>
            <ChevronLeft className="w-3 h-3 rotate-90 text-[#64748B]" />
          </button>
          <button onClick={() => move(i, i + 1)} disabled={i === list.length - 1} className="p-0.5 disabled:opacity-30" aria-label="Move down" data-testid={`${testId}-down-${i}`}>
            <ChevronRight className="w-3 h-3 rotate-90 text-[#64748B]" />
          </button>
          <button
            onClick={() => removeAt(i)}
            disabled={list.length <= minChoices}
            className="p-0.5 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Remove choice"
            data-testid={`${testId}-del-${i}`}
          >
            <Trash2 className="w-3 h-3 text-red-500" />
          </button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className="text-xs w-full" data-testid={`${testId}-add-btn`}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add Choice
      </Button>
      {list.length <= minChoices && (
        <p className="text-[10px] text-[#94A3B8]">Minimum {minChoices} choices required.</p>
      )}
    </div>
  );
}

// ─── SATISACTION MAPPING (Gap Category / Gap Entry) ───
function SatisactionMapping({ question, gapCategories, onUpdate }) {
  const selectedCat = question.gap_category || null;
  const entries = (gapCategories.find(c => c.category === selectedCat)?.entries) || [];

  return (
    <div className="space-y-2 border-t border-[#E2E8F0] pt-4" data-testid="satisaction-mapping">
      <h4 className="text-xs font-bold text-[#64748B] uppercase flex items-center gap-1.5">
        <Shield className="w-3 h-3" /> satisACTION&reg; Mapping
      </h4>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Gap Category</label>
        <Select value={selectedCat || undefined} onValueChange={(v) => onUpdate(question.id, { gap_category: v === '__none__' ? null : v, gap_entry: null })}>
          <SelectTrigger className="text-sm" data-testid="gap-category-select"><SelectValue placeholder="Select a category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">(None)</SelectItem>
            {gapCategories.map(c => (
              <SelectItem key={c.category} value={c.category} data-testid={`gap-cat-${c.category.replace(/\s+/g, '-').toLowerCase()}`}>{c.category}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Gap Entry</label>
        <Select
          value={question.gap_entry || undefined}
          onValueChange={(v) => onUpdate(question.id, { gap_entry: v === '__none__' ? null : v })}
          disabled={!selectedCat}
        >
          <SelectTrigger className="text-sm" data-testid="gap-entry-select">
            <SelectValue placeholder={selectedCat ? 'Select an entry' : 'Select a Gap Category first'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">(None)</SelectItem>
            {entries.map(e => (
              <SelectItem key={e.name} value={e.name}>{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── TRUSTX QUALIFIER ───
function TrustXQualifierPicker({ question, survey, onUpdate }) {
  const qualifiers = survey?.qualifiers || [];
  return (
    <div className="space-y-2 border-t border-[#E2E8F0] pt-4" data-testid="trustx-qualifier">
      <h4 className="text-xs font-bold text-[#64748B] uppercase flex items-center gap-1.5">
        <Tag className="w-3 h-3" /> TrustX&reg; Qualifier
      </h4>
      <Select
        value={question.qualifier_id || undefined}
        onValueChange={(v) => onUpdate(question.id, { qualifier_id: v === '__none__' ? null : v })}
      >
        <SelectTrigger className="text-sm" data-testid="qualifier-select"><SelectValue placeholder="(Optional) Select a qualifier" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">(None)</SelectItem>
          {qualifiers.map(q => (
            <SelectItem key={q.id} value={q.id}>{q.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-[10px] text-[#94A3B8]">Questions without a qualifier do not contribute to the TrustX&reg; Score.</p>
    </div>
  );
}

// ─── MAIN QUESTION SETTINGS ───
function QuestionSettings({ question, questions, survey, surveyId, gapCategories, onUpdate, onSurveyUpdate }) {
  const [text, setText] = useState(question.text || '');
  const [guidance, setGuidance] = useState(question.guidance_note || '');
  const [placeholder, setPlaceholder] = useState(question.placeholder_text || '');
  const [voiceHelperText, setVoiceHelperText] = useState(question.voice_helper_text || '');

  useEffect(() => {
    setText(question.text || '');
    setGuidance(question.guidance_note || '');
    setPlaceholder(question.placeholder_text || '');
    setVoiceHelperText(question.voice_helper_text || '');
  }, [question.id, question.text, question.guidance_note, question.placeholder_text, question.voice_helper_text]);

  const commitText = () => { if (text !== (question.text || '')) onUpdate(question.id, { text }); };
  const commitGuidance = () => { if (guidance !== (question.guidance_note || '')) onUpdate(question.id, { guidance_note: guidance }); };
  const commitPlaceholder = () => { if (placeholder !== (question.placeholder_text || '')) onUpdate(question.id, { placeholder_text: placeholder }); };
  const commitVoiceHelperText = () => {
    if (voiceHelperText !== (question.voice_helper_text || '')) onUpdate(question.id, { voice_helper_text: voiceHelperText });
  };

  const isTextFirstVoice = question.type === 'voice' && question.response_mode === 'text_first';
  const showPlaceholder = PLACEHOLDER_TYPES.has(question.type) || isTextFirstVoice;
  const isScored = SCORED_TYPES.has(question.type);
  const isChoiceType = CHOICE_TYPES.has(question.type);
  const isYesNo = question.type === 'yes_no';
  const isNonQuestion = NON_QUESTION_TYPES.has(question.type);

  return (
    <div className="space-y-5" data-testid="settings-panel">
      <h3 className="font-bold text-[#0F172A] text-sm">Question Settings</h3>

      {/* Type badge (read-only) */}
      <div className="text-[11px] text-[#64748B] bg-[#F8FAFC] rounded px-2 py-1 border border-[#E2E8F0]">
        Type: <span className="font-semibold text-[#0F172A]">{getTypeConfig(question.type, question).label}</span>
      </div>

      {/* Question Text */}
      {!isNonQuestion && (
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">Question Text</label>
          <RichTextEditor value={text} onChange={(v) => { setText(v); }} testId="question-text" rows={2} />
          <div className="mt-1 text-right"><button onClick={commitText} className="text-[10px] text-[#1A3E6F] hover:underline" data-testid="save-question-text">Save text</button></div>
        </div>
      )}

      {/* Section header / rich text block / image display handle their own content */}
      {question.type === 'section_header' && (
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">Section Title</label>
          <Input defaultValue={question.text || ''} onBlur={(e) => { if (e.target.value !== question.text) onUpdate(question.id, { text: e.target.value }); }} className="text-sm" data-testid="section-title-input" />
          <label className="text-xs font-medium text-[#64748B] block mb-1 mt-3">Description (optional)</label>
          <Textarea defaultValue={question.sub_text || ''} onBlur={(e) => { if (e.target.value !== (question.sub_text || '')) onUpdate(question.id, { sub_text: e.target.value }); }} rows={2} className="text-sm" data-testid="section-description-input" />
        </div>
      )}

      {question.type === 'rich_text_block' && (
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">Rich Text Content</label>
          <RichTextEditor
            value={question.rich_content || ''}
            onChange={(v) => onUpdate(question.id, { rich_content: v })}
            testId="rich-block-content" rows={4}
          />
        </div>
      )}

      {question.type === 'image_display' && (
        <ImageDisplaySettings question={question} onUpdate={onUpdate} />
      )}

      {/* Guidance Note */}
      {!isNonQuestion && (
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">Guidance Note</label>
          <RichTextEditor value={guidance} onChange={(v) => setGuidance(v)} testId="guidance-note" rows={2} />
          <div className="mt-1 text-right"><button onClick={commitGuidance} className="text-[10px] text-[#1A3E6F] hover:underline" data-testid="save-guidance-note">Save note</button></div>
          <p className="text-[10px] text-[#94A3B8] mt-1">Shown below the question text in a smaller muted style.</p>
        </div>
      )}

      {/* Placeholder */}
      {showPlaceholder && (
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">Placeholder Text</label>
          <Input value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} onBlur={commitPlaceholder} className="text-sm" placeholder="Hint text inside the input" data-testid="placeholder-input" />
        </div>
      )}

      {/* Voice helper text — caption shown under a voice question explaining
          that respondents can tap Record OR Type. Blank = hide. */}
      {question.type === 'voice' && !isTextFirstVoice && (
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">Helper text below the question <span className="text-[#94A3B8] font-normal">(optional)</span></label>
          <Input
            value={voiceHelperText}
            onChange={(e) => setVoiceHelperText(e.target.value)}
            onBlur={commitVoiceHelperText}
            className="text-sm"
            placeholder="e.g. Record your voice or type your answer — either works."
            data-testid="voice-helper-text-input"
          />
          <p className="text-[10px] text-[#94A3B8] mt-1">Leave blank to hide. Shown italicised under the question text.</p>
        </div>
      )}

      {/* Required */}
      {!isNonQuestion && (
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-[#64748B]">Required</label>
          <Switch checked={!!question.is_required} onCheckedChange={(v) => onUpdate(question.id, { is_required: v })} data-testid="required-toggle" />
        </div>
      )}

      {/* Administrative / report-identification field — sets
          `system_field: "administrative"` so the question is excluded
          from Experience Performance. Available only for choice-style
          questions eligible for Experience Performance. Sending `null`
          on unmark is safe: the backend only clears the marker when it
          currently equals `"administrative"`, so recognised values like
          `"nps"` are preserved. */}
      {ADMIN_TAGGABLE_TYPES.has(question.type) && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <label className="text-xs font-medium text-[#64748B]">Administrative / report-identification field</label>
            <p className="text-[10px] text-[#94A3B8] mt-0.5" data-testid="admin-field-note">
              Excluded from Experience Performance.
            </p>
          </div>
          <Switch
            checked={(question.system_field || '').toString().toLowerCase() === 'administrative'}
            onCheckedChange={(v) => onUpdate(question.id, { system_field: v ? 'administrative' : null })}
            data-testid="admin-field-toggle"
          />
        </div>
      )}

      {/* Commentable */}
      {COMMENTABLE_TYPES.has(question.type) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-xs font-medium text-[#64748B]">Commentable</label>
              <p className="text-[10px] text-[#94A3B8] mt-0.5">Show a comment box below the answer.</p>
            </div>
            <Switch checked={!!question.commentable} onCheckedChange={(v) => onUpdate(question.id, { commentable: v })} data-testid="commentable-toggle" />
          </div>
          {question.commentable && question.is_required && (
            <div className="flex items-center justify-between pl-3 border-l-2 border-[#E2E8F0]">
              <div>
                <label className="text-xs font-medium text-[#64748B]">Require comment</label>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">Respondents must type a comment before continuing.</p>
              </div>
              <Switch
                checked={!!question.comment_required}
                onCheckedChange={(v) => onUpdate(question.id, { comment_required: v })}
                data-testid="comment-required-toggle"
              />
            </div>
          )}
        </div>
      )}

      {/* Conditional visibility — only on choice-style questions. */}
      {['dropdown', 'single_choice', 'button_choice', 'yes_no'].includes(question.type) && (
        <ConditionalVisibilityPanel
          question={question}
          allQuestions={questions}
          onUpdate={onUpdate}
        />
      )}

      {/* Type-specific */}
      {question.type === 'voice' && <VoiceSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'star_rating' && <StarSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'rating_scale' && <RatingScaleSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'number_input' && <NumberInputSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'number_input' && <FinancialRoleSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'nps' && <NPSDisplaySettings question={question} onUpdate={onUpdate} />}
      {question.type === 'file_upload' && <FileUploadSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'confirmation' && <ConfirmationSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'contact_info' && <ContactSettingsV2 question={question} onUpdate={onUpdate} />}
      {question.type === 'welcome' && <WelcomeSettingsPanel question={question} onUpdate={onUpdate} />}
      {question.type === 'thank_you' && <ThankYouSettingsPanel question={question} onUpdate={onUpdate} survey={survey} onSurveyUpdate={onSurveyUpdate} />}
      {question.type === 'matrix' && <MatrixSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'emoji_rating' && <EmojiRatingSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'ranking' && <RankingSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'image_choice' && <ImageChoiceSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'media_question' && <MediaQuestionSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'attribute_ratings' && <AttributeRatingsSettings question={question} onUpdate={onUpdate} />}
      {question.type === 'contact_preference' && <ContactPreferenceSettings question={question} questions={questions} onUpdate={onUpdate} />}
      {CLASSIFIABLE_TYPES.has(question.type) && <ClassificationSettings question={question} onUpdate={onUpdate} />}

      {/* Choices for dropdown/single/multi/button */}
      {isChoiceType && (
        <ChoiceEditor
          choices={question.options || []}
          withScore={question.type !== 'dropdown'}
          onChange={(newList) => onUpdate(question.id, {
            options: newList,
            ...(question.option_followups?.length ? { option_followups: remapFollowups(question.options, newList, question.option_followups) } : {}),
          })}
        />
      )}
      {question.type === 'single_choice' && <ChoiceFollowupSettings question={question} onUpdate={onUpdate} />}

      {/* Yes/No — configurable labels & scores */}
      {isYesNo && (() => {
        const opts = Array.isArray(question.options) && question.options.length >= 2
          ? question.options
          : [{ label: 'Yes', score_value: 1 }, { label: 'No', score_value: 0 }];
        const a = opts[0] || { label: 'Yes', score_value: 1 };
        const b = opts[1] || { label: 'No', score_value: 0 };
        const commit = (idx, patch) => {
          const next = [{ ...a }, { ...b }];
          next[idx] = { ...next[idx], ...patch };
          onUpdate(question.id, { options: next });
        };
        return (
          <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="yesno-settings">
            <h4 className="text-xs font-bold text-[#64748B] uppercase">Yes / No Options</h4>
            <div className="grid grid-cols-[1fr_80px] gap-2">
              <div>
                <label className="text-[10px] text-[#64748B]">Label A</label>
                <Input
                  defaultValue={a.label ?? 'Yes'}
                  onBlur={(e) => {
                    const v = e.target.value.trim() || 'Yes';
                    if (v !== (a.label ?? 'Yes')) commit(0, { label: v });
                  }}
                  className="text-xs h-8"
                  data-testid="yesno-label-a"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#64748B]">Score A</label>
                <Input
                  type="number"
                  defaultValue={a.score_value ?? 1}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    const n = raw === '' ? 1 : Number(raw);
                    if (!isNaN(n) && n !== (a.score_value ?? 1)) commit(0, { score_value: n });
                  }}
                  className="text-xs h-8 text-center"
                  data-testid="yesno-score-a"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#64748B]">Label B</label>
                <Input
                  defaultValue={b.label ?? 'No'}
                  onBlur={(e) => {
                    const v = e.target.value.trim() || 'No';
                    if (v !== (b.label ?? 'No')) commit(1, { label: v });
                  }}
                  className="text-xs h-8"
                  data-testid="yesno-label-b"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#64748B]">Score B</label>
                <Input
                  type="number"
                  defaultValue={b.score_value ?? 0}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    const n = raw === '' ? 0 : Number(raw);
                    if (!isNaN(n) && n !== (b.score_value ?? 0)) commit(1, { score_value: n });
                  }}
                  className="text-xs h-8 text-center"
                  data-testid="yesno-score-b"
                />
              </div>
            </div>
            <p className="text-[10px] text-[#94A3B8]">Score values contribute to the TrustX&reg; Score calculation.</p>
          </div>
        );
      })()}

      {/* satisACTION mapping for scored questions; TrustX® Qualifier now available on ALL question types (Part B1) */}
      {isScored && <SatisactionMapping question={question} gapCategories={gapCategories} onUpdate={onUpdate} />}
      <TrustXQualifierPicker question={question} survey={survey} onUpdate={onUpdate} />

      {/* Conditional logic */}
      {!isNonQuestion && <LogicRulesPanel question={question} questions={questions} onUpdate={onUpdate} />}

      {/* Platform-admin only: question ID with copy-to-clipboard. Sits at
          the bottom of the settings panel above any future save/delete
          buttons. Read-only — wraps in a role check internally, so when
          the viewer isn't a platform_admin the row renders nothing. */}
      <AdminIdRow
        questionId={question.id}
        showLabel
        testId="settings-question-id"
      />
    </div>
  );
}

/* =============== Conditional visibility ===============
 * Shown for dropdown / single_choice / button_choice / yes_no questions.
 * Stored on the question as
 *   condition: { question_id, operator: "eq"|"neq", value }
 * The SurveyForm evaluates this at runtime to hide and skip the question
 * when its parent answer does not satisfy the rule.
 */
function ConditionalVisibilityPanel({ question, allQuestions, onUpdate }) {
  const cond = question.condition || null;
  const enabled = !!cond;

  // Eligible source questions: earlier in survey order, choice-style with
  // defined options. Yes/No too.
  const idx = (allQuestions || []).findIndex(q => q.id === question.id);
  const sources = (allQuestions || [])
    .slice(0, idx >= 0 ? idx : 0)
    .filter(q => ['dropdown', 'single_choice', 'button_choice', 'yes_no'].includes(q.type))
    .filter(q => Array.isArray(q.options) && q.options.length > 0);

  const sourceQ = cond?.question_id
    ? (allQuestions || []).find(q => q.id === cond.question_id)
    : null;

  const toggle = (v) => {
    if (v) {
      const first = sources[0];
      if (!first) {
        // No eligible source — keep toggle off and surface a hint.
        onUpdate(question.id, { condition: null });
        return;
      }
      const firstOpt = (first.options || [])[0];
      const firstVal = firstOpt?.label != null ? firstOpt.label : (firstOpt?.value ?? '');
      onUpdate(question.id, {
        condition: { question_id: first.id, operator: 'eq', value: firstVal },
      });
    } else {
      onUpdate(question.id, { condition: null });
    }
  };

  const setField = (patch) => {
    const nextSource = patch.question_id
      ? (allQuestions || []).find(q => q.id === patch.question_id)
      : sourceQ;
    const next = { ...(cond || {}), ...patch };
    // When swapping the source question, reset the value to the first
    // option of the new source so the UI never shows a stale value.
    if (patch.question_id && nextSource) {
      const firstOpt = (nextSource.options || [])[0];
      next.value = firstOpt?.label != null ? firstOpt.label : (firstOpt?.value ?? '');
    }
    if (!next.operator) next.operator = 'eq';
    onUpdate(question.id, { condition: next });
  };

  return (
    <div className="border border-[#E2E8F0] rounded-md p-3 space-y-3" data-testid="conditional-visibility-panel">
      <div className="flex items-center justify-between gap-2">
        <div>
          <label className="text-xs font-semibold text-[#0F172A]">Show this question only if...</label>
          <p className="text-[10px] text-[#94A3B8] mt-0.5">Hide this question unless a previous answer matches.</p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={toggle}
          disabled={!enabled && sources.length === 0}
          data-testid="condition-toggle"
        />
      </div>
      {!enabled && sources.length === 0 && (
        <p className="text-[10px] text-[#D62B27]">Add a dropdown, single choice, button choice, or yes/no question above this one first.</p>
      )}
      {enabled && cond && (
        <div className="grid grid-cols-1 gap-2">
          <div>
            <label className="text-[10px] text-[#64748B]">Question</label>
            <Select
              value={cond.question_id || ''}
              onValueChange={(v) => setField({ question_id: v })}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="condition-source">
                <SelectValue placeholder="Select a question" />
              </SelectTrigger>
              <SelectContent>
                {sources.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`condition-source-opt-${s.id}`}>
                    {(s.text || s.type).replace(/<[^>]+>/g, '').slice(0, 60) || s.type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-[#64748B]">Operator</label>
            <Select
              value={cond.operator || 'eq'}
              onValueChange={(v) => setField({ operator: v })}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="condition-operator">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="eq" data-testid="condition-op-eq">is equal to</SelectItem>
                <SelectItem value="neq" data-testid="condition-op-neq">is not equal to</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-[#64748B]">Value</label>
            <Select
              value={String(cond.value ?? '')}
              onValueChange={(v) => setField({ value: v })}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="condition-value">
                <SelectValue placeholder="Choose an answer" />
              </SelectTrigger>
              <SelectContent>
                {(sourceQ?.options || []).map((opt, i) => {
                  const v = opt?.label != null ? opt.label : (opt?.value ?? '');
                  return (
                    <SelectItem key={`${v}-${i}`} value={String(v)} data-testid={`condition-value-opt-${i}`}>
                      {String(v)}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── TYPE-SPECIFIC SETTING PANELS ───

function VoiceSettings({ question, onUpdate }) {
  const vs = question.voice_settings || { max_duration_seconds: 120, allow_text_fallback: true };
  const update = (field, val) => onUpdate(question.id, { voice_settings: { ...vs, [field]: val } });

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="voice-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Voice Settings</h4>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Max Recording Length</label>
        <Select value={String(vs.max_duration_seconds)} onValueChange={(v) => update('max_duration_seconds', parseInt(v))}>
          <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 seconds</SelectItem>
            <SelectItem value="60">60 seconds</SelectItem>
            <SelectItem value="90">90 seconds</SelectItem>
            <SelectItem value="120">120 seconds</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {question.response_mode !== 'text_first' && (
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-[#64748B]">Allow text fallback</label>
          <Switch checked={vs.allow_text_fallback} onCheckedChange={(v) => update('allow_text_fallback', v)} />
        </div>
      )}
    </div>
  );
}

function StarSettings({ question, onUpdate }) {
  const ss = question.star_settings || { max_stars: 5, label: '' };
  const update = (field, val) => onUpdate(question.id, { star_settings: { ...ss, [field]: val } });
  const [label, setLabel] = useState(ss.label);
  useEffect(() => { setLabel(ss.label); }, [ss.label]);

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="star-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Star Rating Settings</h4>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Number of Stars</label>
        <Select value={String(ss.max_stars)} onValueChange={(v) => update('max_stars', parseInt(v))}>
          <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="3">3 stars</SelectItem>
            <SelectItem value="4">4 stars</SelectItem>
            <SelectItem value="5">5 stars</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Label</label>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => update('label', label)} className="text-sm" placeholder="e.g. Food Quality" />
      </div>
    </div>
  );
}

function RatingScaleSettings({ question, onUpdate }) {
  const rs = question.rating_scale || { min_value: 1, max_value: 5, min_label: 'Very Dissatisfied', max_label: 'Very Satisfied' };
  const [minV, setMinV] = useState(String(rs.min_value));
  const [maxV, setMaxV] = useState(String(rs.max_value));
  const [minL, setMinL] = useState(rs.min_label || '');
  const [maxL, setMaxL] = useState(rs.max_label || '');
  useEffect(() => {
    setMinV(String(rs.min_value)); setMaxV(String(rs.max_value));
    setMinL(rs.min_label || ''); setMaxL(rs.max_label || '');
  }, [rs.min_value, rs.max_value, rs.min_label, rs.max_label]);
  const commit = (patch) => onUpdate(question.id, { rating_scale: { ...rs, ...patch } });

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="rating-scale-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Rating Scale</h4>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-[#64748B]">Min</label>
          <Input type="number" value={minV} onChange={(e) => setMinV(e.target.value)} onBlur={() => commit({ min_value: Number(minV) || 1 })} className="text-xs h-8" data-testid="rating-min-value" />
        </div>
        <div>
          <label className="text-[10px] text-[#64748B]">Max</label>
          <Input type="number" value={maxV} onChange={(e) => setMaxV(e.target.value)} onBlur={() => commit({ max_value: Number(maxV) || 5 })} className="text-xs h-8" data-testid="rating-max-value" />
        </div>
      </div>
      <div>
        <label className="text-[10px] text-[#64748B]">Min Label</label>
        <Input value={minL} onChange={(e) => setMinL(e.target.value)} onBlur={() => commit({ min_label: minL })} placeholder="Very Dissatisfied" className="text-xs h-8" data-testid="rating-min-label" />
      </div>
      <div>
        <label className="text-[10px] text-[#64748B]">Max Label</label>
        <Input value={maxL} onChange={(e) => setMaxL(e.target.value)} onBlur={() => commit({ max_label: maxL })} placeholder="Very Satisfied" className="text-xs h-8" data-testid="rating-max-label" />
      </div>
      <p className="text-[10px] text-[#94A3B8]">Range 1-5 with standard CSAT labels feeds into the CSAT engine automatically.</p>
    </div>
  );
}

function NumberInputSettings({ question, onUpdate }) {
  const ns = question.number_settings || { min_value: null, max_value: null, step: 1 };
  const update = (patch) => onUpdate(question.id, { number_settings: { ...ns, ...patch } });
  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="number-input-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Number Input</h4>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] text-[#64748B]">Min</label>
          <Input type="number" defaultValue={ns.min_value ?? ''} onBlur={(e) => update({ min_value: e.target.value === '' ? null : Number(e.target.value) })} className="text-xs h-8" data-testid="number-min" />
        </div>
        <div>
          <label className="text-[10px] text-[#64748B]">Max</label>
          <Input type="number" defaultValue={ns.max_value ?? ''} onBlur={(e) => update({ max_value: e.target.value === '' ? null : Number(e.target.value) })} className="text-xs h-8" data-testid="number-max" />
        </div>
        <div>
          <label className="text-[10px] text-[#64748B]">Step</label>
          <Input type="number" defaultValue={ns.step ?? 1} onBlur={(e) => update({ step: e.target.value === '' ? 1 : Number(e.target.value) })} className="text-xs h-8" data-testid="number-step" />
        </div>
      </div>
    </div>
  );
}

// ─── Phase 2: Emoji / Smiley Rating settings ───
// Deterministic 1..N answer serialization (matches star_rating / rating_scale).
function EmojiRatingSettings({ question, onUpdate }) {
  const es = question.emoji_settings || { scale: 5, labels: [] };
  const scale = [3, 5].includes(Number(es.scale)) ? Number(es.scale) : 5;
  const labels = Array.isArray(es.labels) ? es.labels : [];
  const setScale = (v) => onUpdate(question.id, { emoji_settings: { ...es, scale: Number(v), labels: labels.slice(0, Number(v)) } });
  const setLabel = (idx, val) => {
    const next = new Array(scale).fill('').map((_, i) => labels[i] || '');
    next[idx] = val;
    onUpdate(question.id, { emoji_settings: { ...es, scale, labels: next } });
  };
  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="emoji-rating-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Emoji Rating</h4>
      <div>
        <label className="text-[10px] text-[#64748B] block mb-1">Scale</label>
        <div className="flex gap-2">
          {[3, 5].map(n => (
            <label key={n} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="radio" name={`emoji-scale-${question.id}`} checked={scale === n} onChange={() => setScale(n)} data-testid={`emoji-scale-${n}`} />
              {n}-point
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] text-[#64748B] block mb-1">Optional labels (leave blank to hide)</label>
        <div className="grid grid-cols-1 gap-1.5">
          {Array.from({ length: scale }).map((_, i) => (
            <Input
              key={i}
              defaultValue={labels[i] || ''}
              placeholder={`Label for ${i + 1}`}
              className="text-xs h-8"
              onBlur={(e) => setLabel(i, e.target.value)}
              data-testid={`emoji-label-${i + 1}`}
            />
          ))}
        </div>
      </div>
      <p className="text-[10px] text-[#94A3B8]">Stored as 1..{scale}. Neutral styling; no colour coding.</p>
    </div>
  );
}

// ─── Phase 2: Ranking option editor ───
// Options come through the shared `options` field so existing question
// versioning + duplication picks them up automatically. Each option
// keeps a stable `id` so responses can reference it deterministically
// regardless of label edits later.
function makeOptionId() {
  return 'opt_' + Math.random().toString(36).slice(2, 10);
}

function RankingSettings({ question, onUpdate }) {
  const options = Array.isArray(question.options) ? question.options : [];
  // Backfill stable IDs on legacy option shapes.
  const withIds = options.map((o) => (typeof o === 'string')
    ? { id: makeOptionId(), label: o }
    : { id: o.id || makeOptionId(), label: o.label ?? '' });

  const commit = (next) => onUpdate(question.id, { options: next });
  const setLabel = (idx, val) => commit(withIds.map((o, i) => i === idx ? { ...o, label: val } : o));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= withIds.length) return;
    const next = [...withIds]; [next[idx], next[j]] = [next[j], next[idx]];
    commit(next);
  };
  const remove = (idx) => commit(withIds.filter((_, i) => i !== idx));
  const add = () => commit([...withIds, { id: makeOptionId(), label: `Option ${withIds.length + 1}` }]);

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="ranking-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Options (respondent will rank)</h4>
      <p className="text-[10px] text-[#94A3B8]">Order below is the initial order shown to the respondent. Response is stored as the ordered list of option IDs.</p>
      <div className="space-y-1.5" data-testid="ranking-options-list">
        {withIds.map((o, i) => (
          <div key={o.id} className="flex items-center gap-1.5" data-testid={`ranking-option-${i}`}>
            <span className="text-[10px] text-[#94A3B8] w-4 text-center">{i + 1}</span>
            <Input
              defaultValue={o.label}
              onBlur={(e) => { const v = e.target.value.trim(); if (v !== o.label) setLabel(i, v); }}
              className="text-xs h-8 flex-1"
              data-testid={`ranking-option-label-${i}`}
            />
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-[10px] px-1 py-0.5 text-[#64748B] disabled:opacity-30" data-testid={`ranking-option-up-${i}`}>↑</button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === withIds.length - 1} className="text-[10px] px-1 py-0.5 text-[#64748B] disabled:opacity-30" data-testid={`ranking-option-down-${i}`}>↓</button>
            <button type="button" onClick={() => remove(i)} className="text-[10px] text-[#D62B27]" data-testid={`ranking-option-remove-${i}`}>×</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="text-[11px] text-[#1A3E6F] font-semibold hover:underline" data-testid="ranking-option-add">+ Add option</button>
    </div>
  );
}

// ─── Phase 2: Image Choice option editor ───
// Each option: { id, label, image_url }. Mode: single | multiple.
// Responses store the selected option {id, label, score_value?} or an
// array of them, mirroring existing single/multiple_choice conventions
// so downstream reporting is compatible.
function ImageChoiceSettings({ question, onUpdate }) {
  const cfg = question.image_choice_settings || { mode: 'single' };
  const options = Array.isArray(question.options) ? question.options : [];
  const withIds = options.map((o) => (typeof o === 'string')
    ? { id: makeOptionId(), label: o, image_url: '' }
    : { id: o.id || makeOptionId(), label: o.label ?? '', image_url: o.image_url || '' });
  const commit = (next) => onUpdate(question.id, { options: next });
  const setMode = (v) => onUpdate(question.id, { image_choice_settings: { ...cfg, mode: v } });
  const setLabel = (idx, val) => commit(withIds.map((o, i) => i === idx ? { ...o, label: val } : o));
  const remove = (idx) => commit(withIds.filter((_, i) => i !== idx));
  const add = () => commit([...withIds, { id: makeOptionId(), label: `Option ${withIds.length + 1}`, image_url: '' }]);

  const uploadForOption = async (idx, file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error('Only JPG/PNG/WEBP'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    try {
      const form = new FormData(); form.append('file', file);
      const r = await fetch(`${API}/surveys/builder/upload-image`, { method: 'POST', credentials: 'include', body: form });
      if (!r.ok) { const err = await r.json().catch(() => ({})); toast.error(err.detail || 'Upload failed'); return; }
      const d = await r.json();
      commit(withIds.map((o, i) => i === idx ? { ...o, image_url: d.image_url } : o));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
  };

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="image-choice-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Image Choice</h4>
      <div>
        <label className="text-[10px] text-[#64748B] block mb-1">Selection mode</label>
        <div className="flex gap-3">
          {[['single', 'Single selection'], ['multiple', 'Multiple selection']].map(([val, lbl]) => (
            <label key={val} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="radio" name={`imgc-mode-${question.id}`} checked={cfg.mode === val} onChange={() => setMode(val)} data-testid={`imgc-mode-${val}`} />
              {lbl}
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-2" data-testid="imgc-options-list">
        {withIds.map((o, i) => (
          <div key={o.id} className="border border-[#E2E8F0] rounded-lg p-2 space-y-1.5" data-testid={`imgc-option-${i}`}>
            <div className="flex items-center gap-2">
              {o.image_url
                ? <img src={o.image_url} alt="" className="w-14 h-14 object-cover rounded border border-[#E2E8F0]" data-testid={`imgc-option-preview-${i}`} />
                : <div className="w-14 h-14 rounded border-2 border-dashed border-[#CBD5E1] flex items-center justify-center text-[10px] text-[#94A3B8]">no img</div>}
              <Input defaultValue={o.label} onBlur={(e) => { const v = e.target.value.trim(); if (v !== o.label) setLabel(i, v); }} className="text-xs h-8 flex-1" placeholder="Optional label" data-testid={`imgc-option-label-${i}`} />
              <button type="button" onClick={() => remove(i)} className="text-[10px] text-[#D62B27]" data-testid={`imgc-option-remove-${i}`}>×</button>
            </div>
            <label className="flex items-center justify-center gap-2 px-2 py-1.5 rounded border-2 border-dashed text-[10px] cursor-pointer hover:bg-[#F8FAFC] border-[#CBD5E1]">
              <Upload className="w-3 h-3 text-[#64748B]" />
              <span className="text-[#64748B]">{o.image_url ? 'Replace image' : 'Upload image'}</span>
              <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadForOption(i, (e.target.files || [])[0])} data-testid={`imgc-option-input-${i}`} />
            </label>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="text-[11px] text-[#1A3E6F] font-semibold hover:underline" data-testid="imgc-option-add">+ Add option</button>
    </div>
  );
}

function FileUploadSettings({ question, onUpdate }) {
  // B2: full configuration — allowed_types (multi), max_files (1-10),
  // max_file_size_mb (1/2/5/10/20/50). Defaults preserved when no config is set
  // so legacy questions render gracefully (image-only, 1 file, 5MB).
  // Fix 4a: 'video' is now a third selectable type.
  const fs = question.file_settings || {};
  const allowedTypes = Array.isArray(fs.allowed_types) && fs.allowed_types.length
    ? fs.allowed_types
    : (fs.accepted === 'pdf' ? ['pdf'] : fs.accepted === 'any' ? ['image', 'pdf'] : ['image']);
  const maxFiles = Math.max(1, Math.min(10, Number(fs.max_files) || 1));
  const maxSizeMb = [1, 2, 5, 10, 20, 50].includes(Number(fs.max_file_size_mb))
    ? Number(fs.max_file_size_mb)
    : ([1, 2, 5, 10, 20, 50].includes(Number(fs.max_size_mb)) ? Number(fs.max_size_mb) : 5);

  const update = (patch) => onUpdate(question.id, {
    file_settings: { ...fs, allowed_types: allowedTypes, max_files: maxFiles, max_file_size_mb: maxSizeMb, ...patch }
  });

  const toggleType = (t) => {
    const next = allowedTypes.includes(t) ? allowedTypes.filter(x => x !== t) : [...allowedTypes, t];
    update({ allowed_types: next.length ? next : ['image'] });
  };

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="file-upload-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">File Upload</h4>
      <div>
        <label className="text-xs text-[#64748B] block mb-1">Allowed file types</label>
        <div className="flex flex-col gap-1.5" data-testid="file-allowed-types">
          <label className="flex items-center gap-2 text-xs text-[#0F172A]">
            <input type="checkbox" checked={allowedTypes.includes('image')} onChange={() => toggleType('image')} data-testid="file-allow-image" />
            Images (JPG, PNG, WEBP)
          </label>
          <label className="flex items-center gap-2 text-xs text-[#0F172A]">
            <input type="checkbox" checked={allowedTypes.includes('pdf')} onChange={() => toggleType('pdf')} data-testid="file-allow-pdf" />
            PDF
          </label>
          <label className="flex items-center gap-2 text-xs text-[#0F172A]">
            <input type="checkbox" checked={allowedTypes.includes('video')} onChange={() => toggleType('video')} data-testid="file-allow-video" />
            Video (MP4, MOV, WEBM)
          </label>
        </div>
      </div>
      <div>
        <label className="text-xs text-[#64748B] block mb-1">Maximum files (1-10)</label>
        <Input type="number" min="1" max="10" defaultValue={maxFiles} onBlur={(e) => {
          const v = Math.min(10, Math.max(1, Number(e.target.value) || 1));
          update({ max_files: v });
        }} className="text-xs h-8" data-testid="file-max-count" />
      </div>
      <div>
        <label className="text-xs text-[#64748B] block mb-1">Maximum file size</label>
        <Select value={String(maxSizeMb)} onValueChange={(v) => update({ max_file_size_mb: Number(v) })}>
          <SelectTrigger className="text-sm" data-testid="file-max-size"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 MB</SelectItem>
            <SelectItem value="2">2 MB</SelectItem>
            <SelectItem value="5">5 MB</SelectItem>
            <SelectItem value="10">10 MB</SelectItem>
            <SelectItem value="20">20 MB (recommended for video)</SelectItem>
            <SelectItem value="50">50 MB</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[10px] text-[#94A3B8] mt-1">New File Upload questions default to 5MB. Increase to 20MB or 50MB when allowing video.</p>
      </div>
    </div>
  );
}

function ConfirmationSettings({ question, onUpdate }) {
  const cs = question.confirmation_settings || { button_label: 'I Agree' };
  const update = (patch) => onUpdate(question.id, { confirmation_settings: { ...cs, ...patch } });
  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="confirmation-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Confirmation</h4>
      <div>
        <label className="text-xs text-[#64748B] block mb-1">Button Label</label>
        <Input defaultValue={cs.button_label ?? 'I Agree'} onBlur={(e) => update({ button_label: e.target.value || 'I Agree' })} className="text-xs h-8" data-testid="confirm-button-label" />
      </div>
    </div>
  );
}

function ImageDisplaySettings({ question, onUpdate }) {
  const is_ = question.image_settings || { url: '', alt: '' };
  const update = (patch) => onUpdate(question.id, { image_settings: { ...is_, ...patch } });
  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="image-display-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Image</h4>
      <div>
        <label className="text-xs text-[#64748B] block mb-1">Image URL</label>
        <Input defaultValue={is_.url ?? ''} onBlur={(e) => update({ url: e.target.value })} placeholder="https://example.com/image.jpg" className="text-xs h-8" data-testid="image-url" />
      </div>
      <div>
        <label className="text-xs text-[#64748B] block mb-1">Alt text</label>
        <Input defaultValue={is_.alt ?? ''} onBlur={(e) => update({ alt: e.target.value })} placeholder="Describe the image" className="text-xs h-8" data-testid="image-alt" />
      </div>
      {is_.url && <img src={is_.url} alt={is_.alt || ''} className="w-full rounded border border-[#E2E8F0]" />}
    </div>
  );
}

function ContactSettingsV2({ question, onUpdate }) {
  const cf = question.contact_fields || {
    mobile: { enabled: true, required: false },
    email: { enabled: false, required: false },
    first_name: { enabled: false, required: false },
    last_name: { enabled: false, required: false },
  };
  const update = (field, patch) => {
    const next = { ...cf, [field]: { ...(cf[field] || {}), ...patch } };
    onUpdate(question.id, { contact_fields: next });
  };

  const Field = ({ keyName, label }) => {
    const f = cf[keyName] || { enabled: false, required: false };
    return (
      <div className="flex items-center justify-between border border-[#E2E8F0] rounded-md px-2 py-1.5" data-testid={`contact-field-${keyName}`}>
        <span className="text-xs text-[#0F172A]">{label}</span>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-[10px] text-[#64748B]">
            Enabled
            <Switch checked={!!f.enabled} onCheckedChange={(v) => update(keyName, { enabled: v })} data-testid={`contact-${keyName}-enabled`} />
          </label>
          <label className={`flex items-center gap-1 text-[10px] text-[#64748B] ${!f.enabled ? 'opacity-40' : ''}`}>
            Required
            <Switch disabled={!f.enabled} checked={!!f.required} onCheckedChange={(v) => update(keyName, { required: v })} data-testid={`contact-${keyName}-required`} />
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2 border-t border-[#E2E8F0] pt-4" data-testid="contact-info-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Contact Information Fields</h4>
      <Field keyName="mobile" label="Mobile number" />
      <Field keyName="email" label="Email address" />
      <Field keyName="first_name" label="First name" />
      <Field keyName="last_name" label="Last name" />
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-2 py-1.5 mt-2">
        <p className="text-[11px] font-semibold text-[#0F172A] mb-1">Consent section</p>
        <p className="text-[10px] text-[#64748B]">
          Toggle visibility from <span className="font-medium">Settings &rarr; Show consent section</span>. &ldquo;Privacy Policy&rdquo; and &ldquo;Terms of Service&rdquo; links use the URLs from survey footer settings.
        </p>
      </div>
    </div>
  );
}

function ThankYouSettingsPanel({ question, onUpdate, survey, onSurveyUpdate }) {
  const ts = question.thank_you_settings || { message: '', show_review_link: 'never' };
  const [msg, setMsg] = useState(ts.message);
  const [url, setUrl] = useState(ts.review_link_url || '');
  useEffect(() => { setMsg(ts.message); setUrl(ts.review_link_url || ''); }, [ts.message, ts.review_link_url]);

  const update = (field, val) => onUpdate(question.id, { thank_you_settings: { ...ts, [field]: val } });

  // B3: redirect fields stored at SURVEY level (not on the question).
  const redirectUrl = survey?.redirect_url || '';
  const redirectCondition = effectiveRedirectCondition(survey);
  const redirectDelay = survey?.redirect_delay_seconds != null ? Number(survey.redirect_delay_seconds) : 3;
  const [redirectUrlLocal, setRedirectUrlLocal] = useState(redirectUrl);
  useEffect(() => { setRedirectUrlLocal(redirectUrl); }, [redirectUrl]);
  const updateSurvey = (patch) => onSurveyUpdate && onSurveyUpdate(patch);

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="thankyou-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Thank You Settings</h4>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Message</label>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} onBlur={() => update('message', msg)} rows={3} className="text-sm" />
      </div>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Show Review Link</label>
        <Select value={ts.show_review_link} onValueChange={(v) => update('show_review_link', v)}>
          <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="always">Always</SelectItem>
            <SelectItem value="if_promoter">Only if Promoter (NPS&reg; 9-10)</SelectItem>
            <SelectItem value="never">Never</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {ts.show_review_link !== 'never' && (
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">Review Link URL</label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => {
              let v = (url || '').trim();
              if (v && !/^https?:\/\//i.test(v)) {
                v = `https://${v}`;
                setUrl(v);
              }
              update('review_link_url', v);
            }}
            className="text-sm"
            placeholder="https://..."
          />
        </div>
      )}

      {/* Static image shown below the Thank You message (stored on thank_you_settings). */}
      <ThankYouStaticImage image={ts.static_image} onChange={(img) => update('static_image', img)} />

      {/* B3: Post-submission redirect (survey-level fields). */}
      <div className="border-t border-[#E2E8F0] pt-3 space-y-3" data-testid="redirect-settings">
        <h4 className="text-xs font-bold text-[#64748B] uppercase">Post-submission Redirect</h4>
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">Redirect Condition</label>
          <Select value={redirectCondition} onValueChange={(v) => updateSurvey({ redirect_condition: v })}>
            <SelectTrigger className="text-sm" data-testid="redirect-condition"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REDIRECT_CONDITIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`redirect-condition-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {redirectCondition === 'never' && (
            <p className="text-[10px] text-[#94A3B8] mt-1" data-testid="redirect-never-hint">Respondents stay on the Thank You screen. Redirect URL and delay don't apply.</p>
          )}
        </div>
        {redirectCondition !== 'never' && (
          <div>
            <label className="text-xs font-medium text-[#64748B] block mb-1">Redirect URL</label>
            <Input
              value={redirectUrlLocal}
              onChange={(e) => setRedirectUrlLocal(e.target.value)}
              onBlur={() => {
                // Auto-prepend https:// when the user enters a bare host like
                // "example.com/path" so the post-submit redirect navigates to
                // the external site instead of being treated as a relative path
                // (which would 404 on the survey domain).
                let v = (redirectUrlLocal || '').trim();
                if (v && !/^https?:\/\//i.test(v)) {
                  v = `https://${v}`;
                  setRedirectUrlLocal(v);
                }
                if (v !== redirectUrl) updateSurvey({ redirect_url: v });
              }}
              type="url"
              placeholder="https://"
              className="text-sm"
              data-testid="redirect-url"
            />
            <p className="text-[10px] text-[#94A3B8] mt-1">Must be a full URL (we'll add <span className="font-mono">https://</span> if you forget).</p>
          </div>
        )}
        {redirectCondition !== 'never' && redirectUrlLocal && (
          <div>
            <label className="text-xs font-medium text-[#64748B] block mb-1">Redirect Delay</label>
            <Select value={String(redirectDelay)} onValueChange={(v) => updateSurvey({ redirect_delay_seconds: Number(v) })}>
              <SelectTrigger className="text-sm" data-testid="redirect-delay"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Immediately</SelectItem>
                <SelectItem value="3">After 3 seconds</SelectItem>
                <SelectItem value="5">After 5 seconds</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

/* =============== Matrix question — settings panel ===============
 * Lets the tenant configure:
 *  - Items source: "fixed" (manually defined) or "freetext" (entered at survey time)
 *  - Items list (for fixed mode)
 *  - Criteria (the rating dimensions — at least one row)
 *  - Rating scale (preset or "custom" with 2–6 labels)
 */
const MATRIX_SCALE_PRESETS = {
  excellent_poor: { label: 'Excellent / Good / Average / Poor', values: ['Excellent', 'Good', 'Average', 'Poor'] },
  always_never:   { label: 'Always / Usually / Sometimes / Never', values: ['Always', 'Usually', 'Sometimes', 'Never'] },
  agree_disagree: { label: 'Strongly Agree / Agree / Disagree / Strongly Disagree', values: ['Strongly Agree', 'Agree', 'Disagree', 'Strongly Disagree'] },
  stars:          { label: '1 – 5 Stars', values: ['5 Stars', '4 Stars', '3 Stars', '2 Stars', '1 Star'] },
  csat_4pt:       { label: 'Very Satisfied / Satisfied / Unsatisfied / Very Unsatisfied', values: ['Very Satisfied', 'Satisfied', 'Unsatisfied', 'Very Unsatisfied'] },
  expectations_3pt: { label: 'Exceeded expectations / Met expectations / Below expectations', values: ['Exceeded expectations', 'Met expectations', 'Below expectations'] },
  likelihood_4pt: { label: 'Very Likely / Likely / Unlikely / Very Unlikely', values: ['Very Likely', 'Likely', 'Unlikely', 'Very Unlikely'] },
  yes_no:         { label: 'Yes / No', values: ['Yes', 'No'] },
};

function MatrixSettings({ question, onUpdate }) {
  const itemsSource = question.items_source || 'fixed';
  const items = Array.isArray(question.items) ? question.items : [];
  const criteria = Array.isArray(question.criteria) && question.criteria.length > 0
    ? question.criteria
    : [''];
  const scale = question.scale || 'excellent_poor';
  const custom = Array.isArray(question.custom_scale) && question.custom_scale.length >= 2
    ? question.custom_scale
    : ['Excellent', 'Good', 'Average', 'Poor'];

  const setItems = (next) => onUpdate(question.id, { items: next });
  const setCriteria = (next) => onUpdate(question.id, { criteria: next });
  const setCustom = (next) => onUpdate(question.id, { custom_scale: next });

  return (
    <div className="space-y-3 border border-[#E2E8F0] rounded-md p-3" data-testid="matrix-settings">
      <div>
        <label className="text-xs font-semibold text-[#0F172A]">Items source</label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <button
            type="button"
            className={`px-2 py-2 rounded border text-xs ${itemsSource === 'fixed' ? 'border-[#1A3E6F] bg-[#F0F4FA] text-[#1A3E6F] font-semibold' : 'border-[#E2E8F0] text-[#64748B]'}`}
            onClick={() => onUpdate(question.id, { items_source: 'fixed' })}
            data-testid="matrix-source-fixed"
          >Fixed list</button>
          <button
            type="button"
            className={`px-2 py-2 rounded border text-xs ${itemsSource === 'freetext' ? 'border-[#1A3E6F] bg-[#F0F4FA] text-[#1A3E6F] font-semibold' : 'border-[#E2E8F0] text-[#64748B]'}`}
            onClick={() => onUpdate(question.id, { items_source: 'freetext' })}
            data-testid="matrix-source-freetext"
          >Free text entry</button>
        </div>
        <p className="text-[10px] text-[#94A3B8] mt-1">
          {itemsSource === 'fixed'
            ? 'You define the items below. Respondent rates every one.'
            : 'Respondent types their own items at survey time, then rates each.'}
        </p>
      </div>

      {itemsSource === 'fixed' && (
        <div>
          <label className="text-xs font-semibold text-[#0F172A]">Items</label>
          <div className="space-y-1 mt-1">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={it}
                  onChange={(e) => {
                    const next = [...items]; next[i] = e.target.value; setItems(next);
                  }}
                  placeholder={`Item ${i + 1}`}
                  className="text-xs h-8"
                  data-testid={`matrix-item-${i}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                  disabled={items.length <= 1}
                  className="text-[#D62B27] h-8 px-2"
                  data-testid={`matrix-item-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => setItems([...items, ''])}
              data-testid="matrix-item-add"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add item
            </Button>
          </div>
        </div>
      )}

      {itemsSource === 'freetext' && (
        <div
          className="rounded-md border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-3 py-2"
          data-testid="matrix-freetext-note"
        >
          <p className="text-xs text-[#475569] italic">
            Respondents will enter their own items at survey time.
          </p>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-[#0F172A]">Criteria <span className="text-[#94A3B8] font-normal">(at least one)</span></label>
        <div className="space-y-1 mt-1">
          {criteria.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={c}
                onChange={(e) => {
                  const next = [...criteria]; next[i] = e.target.value; setCriteria(next);
                }}
                placeholder={`Criterion ${i + 1} (e.g. Taste)`}
                className="text-xs h-8"
                data-testid={`matrix-criterion-${i}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCriteria(criteria.filter((_, idx) => idx !== i))}
                disabled={criteria.length <= 1}
                className="text-[#D62B27] h-8 px-2"
                data-testid={`matrix-criterion-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setCriteria([...criteria, ''])}
            data-testid="matrix-criterion-add"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add criterion
          </Button>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-[#0F172A]">Rating scale</label>
        <Select value={scale} onValueChange={(v) => onUpdate(question.id, { scale: v })}>
          <SelectTrigger className="h-8 text-xs mt-1" data-testid="matrix-scale-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(MATRIX_SCALE_PRESETS).map(([k, v]) => (
              <SelectItem key={k} value={k} data-testid={`matrix-scale-opt-${k}`}>{v.label}</SelectItem>
            ))}
            <SelectItem value="custom" data-testid="matrix-scale-opt-custom">Custom (2 – 6 labels)</SelectItem>
          </SelectContent>
        </Select>
        {scale === 'custom' && (
          <div className="mt-2 space-y-1 border border-[#E2E8F0] rounded p-2 bg-[#F8FAFC]">
            <p className="text-[10px] text-[#64748B]">Order from best → worst. Min 2, max 6.</p>
            {custom.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={c}
                  onChange={(e) => {
                    const next = [...custom]; next[i] = e.target.value; setCustom(next);
                  }}
                  placeholder={`Label ${i + 1}`}
                  className="text-xs h-8"
                  data-testid={`matrix-custom-${i}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setCustom(custom.filter((_, idx) => idx !== i))}
                  disabled={custom.length <= 2}
                  className="text-[#D62B27] h-8 px-2"
                  data-testid={`matrix-custom-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            {custom.length < 6 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => setCustom([...custom, ''])}
                data-testid="matrix-custom-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add label
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// Welcome-screen website QR: a plain link to the brand's site (never a survey-access QR).
function WebsiteQrFields({ ws, update }) {
  const [url, setUrl] = useState(ws.website_qr_url || '');
  const [label, setLabel] = useState(ws.website_qr_label || '');
  useEffect(() => { setUrl(ws.website_qr_url || ''); setLabel(ws.website_qr_label || ''); }, [ws.website_qr_url, ws.website_qr_label]);
  const saveUrl = () => {
    const v = url.trim();
    if (v && !/^https?:\/\/[^\s]+\.[^\s]+/i.test(v)) { toast.error('Enter a full website address starting with https://'); return; }
    update({ website_qr_url: v });
  };
  return (
    <div className="grid grid-cols-1 gap-2">
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Website QR link (optional)</label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} onBlur={saveUrl} placeholder="https://www.example.com" className="text-sm" data-testid="welcome-website-qr-url" />
        <p className="text-[10px] text-[#94A3B8] mt-1">Shows a QR on the Welcome screen that only opens this website.</p>
      </div>
      {url.trim() && (
        <div>
          <label className="text-xs font-medium text-[#64748B] block mb-1">QR caption</label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => update({ website_qr_label: label.trim() })} placeholder="Visit our website" className="text-sm" data-testid="welcome-website-qr-label" />
        </div>
      )}
    </div>
  );
}


function WelcomeSettingsPanel({ question, onUpdate }) {
  // B1: Welcome Screen settings — title, subtitle, button label, and image
  // upload (JPG/PNG/WEBP up to 5MB) stored as `welcome_image_url`.
  const ws = question.welcome_settings || { title: '', subtitle: '', button_label: 'Start' };
  const [title, setTitle] = useState(ws.title || '');
  const [subtitle, setSubtitle] = useState(ws.subtitle || '');
  const [buttonLabel, setButtonLabel] = useState(ws.button_label || 'Start');
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    setTitle(ws.title || '');
    setSubtitle(ws.subtitle || '');
    setButtonLabel(ws.button_label || 'Start');
  }, [ws.title, ws.subtitle, ws.button_label]);

  const update = (patch) => onUpdate(question.id, { welcome_settings: { ...ws, ...patch } });

  const onPickImage = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG, WEBP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API}/surveys/builder/upload-image`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
        return;
      }
      const data = await res.json();
      onUpdate(question.id, { welcome_image_url: data.image_url });
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const imgUrl = question.welcome_image_url || '';

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="welcome-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Welcome Screen</h4>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Title</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => update({ title })} className="text-sm" data-testid="welcome-title" />
      </div>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Subtitle / description</label>
        <Textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} onBlur={() => update({ subtitle })} rows={3} className="text-sm" data-testid="welcome-subtitle" />
      </div>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Start button label</label>
        <Input value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)} onBlur={() => update({ button_label: buttonLabel || 'Start' })} className="text-sm" data-testid="welcome-button-label" />
      </div>
      <WebsiteQrFields ws={ws} update={update} />
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Image (JPG, PNG, WEBP — max 5MB)</label>
        {imgUrl && (
          <div className="mb-2">
            <img src={imgUrl} alt="Welcome" className="w-full rounded border border-[#E2E8F0]" data-testid="welcome-image-preview" />
            <button
              type="button"
              className="text-[11px] text-[#D62B27] hover:underline mt-1"
              onClick={() => onUpdate(question.id, { welcome_image_url: '' })}
              data-testid="welcome-image-remove"
            >Remove image</button>
          </div>
        )}
        <label className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed text-xs cursor-pointer hover:bg-[#F8FAFC] border-[#CBD5E1]">
          <Upload className="w-3.5 h-3.5 text-[#64748B]" />
          <span className="text-[#64748B]">{uploading ? 'Uploading…' : (imgUrl ? 'Replace image' : 'Upload image')}</span>
          <input
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,image/webp"
            disabled={uploading}
            onChange={(e) => onPickImage((e.target.files || [])[0])}
            data-testid="welcome-image-input"
          />
        </label>
      </div>
    </div>
  );
}

function ContactSettings_DEPRECATED() { return null; }
// eslint-disable-next-line no-unused-expressions
void ContactSettings_DEPRECATED;

// ─── NPS display-style + appearance picker ───
// Three display styles (Numbers only / Faces only / Faces and numbers) and a
// separate Colour / Black & White appearance. Saved per question. Legacy
// values are resolved so the selected option always matches what respondents
// see (fixes the old "Numbers only configured but faces shown" mismatch).
function NPSDisplaySettings({ question, onUpdate }) {
  const { style, appearance } = resolveNpsPresentation(question);
  const showAppearance = style !== 'numbers';
  return (
    <div className="space-y-4 border-t border-[#E2E8F0] pt-4" data-testid="nps-display-settings">
      <div>
        <h4 className="text-xs font-bold text-[#64748B] uppercase mb-2">Display style</h4>
        <div className="space-y-1.5">
          {[
            ['numbers',       'Numbers only',      'Plain 0–10 number buttons, no faces.'],
            ['faces',         'Faces only',        'Faces only; numbers are hidden but still recorded.'],
            ['faces_numbers', 'Faces and numbers', 'Both a face and the number for every score.'],
          ].map(([val, label, hint]) => (
            <label key={val} className="flex items-start gap-2 text-xs text-[#0F172A] cursor-pointer">
              <input
                type="radio"
                name={`nps-display-${question.id}`}
                value={val}
                checked={style === val}
                onChange={() => onUpdate(question.id, { nps_display_style: val })}
                data-testid={`nps-display-${val}`}
              />
              <span className="flex-1">
                <span className="font-medium">{label}</span>
                <span className="block text-[10px] text-[#94A3B8]">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {showAppearance && (
        <div>
          <h4 className="text-xs font-bold text-[#64748B] uppercase mb-2">Face appearance</h4>
          <div className="grid grid-cols-2 gap-2">
            {[['color', 'Colour', 'color'], ['bw', 'Black & White', 'bw']].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => onUpdate(question.id, { nps_face_appearance: val })}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border ${appearance === val ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`}
                data-testid={`nps-appearance-${val}`}
              >
                <NpsFace score={9} appearance={val} size={18} />
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-end justify-center gap-1 pt-1" data-testid="nps-settings-preview">
        {[0, 3, 6, 7, 8, 9, 10].map(s => (
          <div key={s} className="flex flex-col items-center gap-0.5">
            {style !== 'numbers' && <NpsFace score={s} appearance={appearance} size={22} />}
            {style !== 'faces' && <span className="text-[10px] font-semibold text-[#64748B]">{s}</span>}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#94A3B8]">All 11 scores (0–10) are always selectable. Detractor/Passive/Promoter grouping is applied in reporting only. Scoring is unchanged.</p>
    </div>
  );
}

// ─── Phase 1: Number Input Financial Role picker ───
// Adds pure metadata — currency / caps / payable amounts are decided
// later at Project/FIND time. Answering never creates a payment record.
function FinancialRoleSettings({ question, onUpdate }) {
  const value = question.financial_role || 'none';
  return (
    <div className="space-y-2 border-t border-[#E2E8F0] pt-4" data-testid="financial-role-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Financial Role</h4>
      <select
        value={value}
        onChange={(e) => onUpdate(question.id, { financial_role: e.target.value })}
        className="w-full border border-[#E2E8F0] rounded-md text-xs h-8 px-2 bg-white"
        data-testid="financial-role-select"
      >
        <option value="none">None</option>
        <option value="reimbursement_amount">Reimbursement Amount</option>
        <option value="misc_payment_amount">Miscellaneous Payment Amount</option>
      </select>
      <p className="text-[10px] text-[#94A3B8]">
        Respondent enters only a number. Currency, caps and payable amounts are set later at Project/FIND time.
      </p>
    </div>
  );
}

// ─── Media Question (standalone type) builder settings ───
// Creator picks Audio or Video, provides the media by uploading a file OR
// recording in-app (device mic/camera), previews/re-records/replaces it, and
// chooses how the respondent answers: Long Text or Voice Response.
// Encode a mono AudioBuffer as a 16-bit PCM WAV Blob (no external library).
function audioBufferToWav(audioBuffer) {
  const data = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const n = data.length;
  const buffer = new ArrayBuffer(44 + n * 2);
  const view = new DataView(buffer);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + n * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);            // PCM
  view.setUint16(22, 1, true);            // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);            // block align
  view.setUint16(34, 16, true);           // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, n * 2, true);
  let off = 44;
  for (let i = 0; i < n; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    off += 2;
  }
  return new Blob([view], { type: 'audio/wav' });
}

// iOS/iPadOS Safari cannot decode WebM/Opus (or Ogg). Desktop & Android record
// in those formats, so for AUDIO prompts we decode the recording and re-encode
// it to a mono WAV (downsampled to <=22.05kHz to keep well under the 20MB cap)
// that plays on iPhone/iPad while remaining playable everywhere. Pure Web Audio,
// no external encoder. Returns the original blob if conversion isn't possible.
async function convertRecordingToWav(blob) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!AC || !OAC) return blob;
  const arrayBuf = await blob.arrayBuffer();
  const decodeCtx = new AC();
  let decoded;
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuf.slice(0));
  } finally {
    if (decodeCtx.close) { try { decodeCtx.close(); } catch { /* ignore */ } }
  }
  const targetRate = Math.min(decoded.sampleRate || 44100, 22050);
  const frames = Math.max(1, Math.ceil(decoded.duration * targetRate));
  const offline = new OAC(1, frames, targetRate); // 1 channel => auto downmix to mono
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const rendered = await offline.startRendering();
  return audioBufferToWav(rendered);
}

// Reuses the authenticated `/api/surveys/builder/upload-media` storage
// endpoint (audio ≤ 20MB, video ≤ 50MB).
function BuilderMediaRecorder({ kind, onUploaded }) {
  // states: idle | permission | recording | recorded | uploading | error
  const [state, setState] = useState('idle');
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState('');
  const blobRef = useRef(null);
  const mimeRef = useRef('');
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const liveRef = useRef(null);
  const MAX_SECONDS = kind === 'video' ? 120 : 300;

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const releaseDevices = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (liveRef.current) liveRef.current.srcObject = null;
  };

  // Release camera/mic + revoke object URLs on unmount (editor close).
  useEffect(() => () => { stopTimer(); releaseDevices(); if (blobUrl) URL.revokeObjectURL(blobUrl); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset cleanly when the creator flips Audio <-> Video.
  useEffect(() => {
    stopTimer(); releaseDevices();
    setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return ''; });
    blobRef.current = null; setSeconds(0); setState('idle');
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickMimeType = () => {
    const candidates = kind === 'video'
      ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
      : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
    if (typeof window !== 'undefined' && window.MediaRecorder && MediaRecorder.isTypeSupported) {
      for (const c of candidates) { if (MediaRecorder.isTypeSupported(c)) return c; }
    }
    return '';
  };

  const start = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error('Recording is not supported in this browser. Please upload a file instead.');
      setState('error'); return;
    }
    setState('permission');
    try {
      const constraints = kind === 'video' ? { video: true, audio: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (kind === 'video' && liveRef.current) {
        liveRef.current.srcObject = stream; liveRef.current.muted = true;
        await liveRef.current.play().catch(() => {});
      }
      const mimeType = pickMimeType();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stopTimer(); releaseDevices();
        const type = (mr.mimeType || mimeType || (kind === 'video' ? 'video/webm' : 'audio/webm')).split(';')[0].trim();
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) { toast.error('The recording came back empty. Please try again.'); setSeconds(0); setState('idle'); return; }
        blobRef.current = blob; mimeRef.current = type;
        const url = URL.createObjectURL(blob);
        setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
        setState('recorded');
      };
      mr.start();
      setState('recording'); setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds(prev => { const next = prev + 1; if (next >= MAX_SECONDS) stop(); return next; });
      }, 1000);
    } catch {
      stopTimer(); releaseDevices();
      toast.error(kind === 'video'
        ? 'Camera/microphone access was denied or unavailable. Please allow access or upload a file.'
        : 'Microphone access was denied or unavailable. Please allow access or upload a file.');
      setState('error');
    }
  };

  const stop = () => {
    stopTimer();
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
  };

  const reRecord = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(''); blobRef.current = null; setSeconds(0); setState('idle');
  };

  const save = async () => {
    if (!blobRef.current || !blobRef.current.size) { toast.error('Nothing to save — please record again.'); return; }
    setState('uploading');
    try {
      let uploadBlob = blobRef.current;
      let type = (mimeRef.current || (kind === 'video' ? 'video/webm' : 'audio/webm')).split(';')[0].trim();
      // For in-browser AUDIO recordings in an iOS-incompatible format
      // (WebM/Opus or Ogg), transcode to WAV so the prompt is audible on
      // iPhone/iPad. Safari-recorded mp4/m4a and all video are left untouched.
      if (kind === 'audio' && (type.includes('webm') || type.includes('ogg'))) {
        try {
          uploadBlob = await convertRecordingToWav(blobRef.current);
          type = 'audio/wav';
        } catch (e) {
          console.warn('iOS audio conversion failed; uploading original recording.', e);
        }
      }
      const ext = type.includes('wav') ? 'wav'
        : type.includes('mp4') ? (kind === 'video' ? 'mp4' : 'm4a')
        : type.includes('ogg') ? (kind === 'video' ? 'ogv' : 'ogg')
        : type.includes('quicktime') ? 'mov' : 'webm';
      const form = new FormData();
      form.append('file', new File([uploadBlob], `recording.${ext}`, { type }));
      form.append('kind', kind);
      const r = await fetch(`${API}/surveys/builder/upload-media`, { method: 'POST', credentials: 'include', body: form });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed. Your recording was kept — try again.');
        setState('recorded'); // retain recording for retry
        return;
      }
      const d = await r.json();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(''); blobRef.current = null; setSeconds(0);
      onUploaded(d.media_url);
      toast.success(`${kind === 'video' ? 'Video' : 'Audio'} saved`);
      setState('idle');
    } catch {
      toast.error('Upload failed. Your recording was kept — try again.');
      setState('recorded'); // retain recording for retry
    }
  };

  const RecIcon = kind === 'video' ? Video : Mic;

  return (
    <div className="rounded-lg border border-[#E2E8F0] p-3 bg-[#F8FAFC] space-y-2" data-testid="builder-media-recorder">
      {kind === 'video' && (state === 'recording' || state === 'permission') && (
        <video ref={liveRef} playsInline muted className="w-full rounded bg-black" data-testid="builder-media-live-preview" />
      )}

      {state === 'recorded' && blobUrl && (
        kind === 'video'
          ? <video src={blobUrl} controls playsInline className="w-full rounded border border-[#E2E8F0]" data-testid="builder-media-recorded-preview" />
          : <audio src={blobUrl} controls className="w-full" data-testid="builder-media-recorded-preview" />
      )}

      {state === 'idle' && (
        <button type="button" onClick={start} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#8B5CF6' }} data-testid="builder-media-record-btn">
          <RecIcon className="w-3.5 h-3.5" /> Record {kind === 'video' ? 'video' : 'audio'}
        </button>
      )}
      {state === 'permission' && (
        <p className="text-xs text-[#64748B] text-center" data-testid="builder-media-permission-text">Allow {kind === 'video' ? 'camera & microphone' : 'microphone'} access…</p>
      )}
      {state === 'recording' && (
        <button type="button" onClick={stop} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-white bg-[#EF4444]" data-testid="builder-media-stop-btn">
          <Square className="w-3.5 h-3.5 fill-current" /> Stop recording · {seconds}s
        </button>
      )}
      {state === 'recorded' && (
        <div className="space-y-2">
          <p className="text-[11px] text-[#B45309] text-center" data-testid="builder-media-recorded-hint">Recorded — not saved yet</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={save} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#16A34A' }} data-testid="builder-media-save-btn">
              <CheckCircle className="w-3.5 h-3.5" /> Save recording
            </button>
            <button type="button" onClick={reRecord} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-[#475569] border border-[#E2E8F0] bg-white" data-testid="builder-media-rerecord-btn">
              <RecIcon className="w-3.5 h-3.5" /> Re-record
            </button>
          </div>
        </div>
      )}
      {state === 'uploading' && (
        <p className="text-xs text-[#64748B] text-center" data-testid="builder-media-uploading-text">Saving recording…</p>
      )}
      {state === 'error' && (
        <button type="button" onClick={start} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-[#EF4444] border border-[#EF4444] bg-white" data-testid="builder-media-retry-btn">
          <RecIcon className="w-3.5 h-3.5" /> Try recording again
        </button>
      )}
    </div>
  );
}

function MediaQuestionSettings({ question, onUpdate }) {
  const ms = question.media_settings || { kind: 'audio', media_url: '', answer_format: 'long_text' };
  const [uploading, setUploading] = useState(false);

  const patch = (fields) => onUpdate(question.id, { media_settings: { ...ms, ...fields } });
  const setKind = (kind) => { if (kind !== ms.kind) patch({ kind, media_url: '' }); };
  const setFormat = (answer_format) => patch({ answer_format });
  const setUrl = (media_url) => patch({ media_url });

  const uploadFile = async (file) => {
    if (!file) return;
    const maxMb = ms.kind === 'video' ? 50 : 20;
    if (file.size > maxMb * 1024 * 1024) { toast.error(`${ms.kind === 'video' ? 'Video' : 'Audio'} must be under ${maxMb}MB`); return; }
    setUploading(true);
    try {
      const form = new FormData(); form.append('file', file); form.append('kind', ms.kind);
      const r = await fetch(`${API}/surveys/builder/upload-media`, { method: 'POST', credentials: 'include', body: form });
      if (!r.ok) { const err = await r.json().catch(() => ({})); toast.error(err.detail || 'Upload failed'); return; }
      const d = await r.json();
      setUrl(d.media_url);
      toast.success(`${ms.kind === 'video' ? 'Video' : 'Audio'} uploaded`);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const hasMedia = !!(ms.media_url || '').trim();
  const accept = ms.kind === 'video' ? 'video/mp4,video/quicktime,video/webm,video/ogg' : 'audio/mpeg,audio/mp4,audio/aac,audio/wav,audio/ogg,audio/webm';

  return (
    <div className="space-y-4 border-t border-[#E2E8F0] pt-4" data-testid="media-question-settings">
      {/* Kind */}
      <div>
        <label className="text-xs font-bold text-[#64748B] uppercase block mb-2">Media type</label>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setKind('audio')} className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border ${ms.kind === 'audio' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`} data-testid="media-kind-audio">
            <Mic className="w-3.5 h-3.5" /> Audio
          </button>
          <button type="button" onClick={() => setKind('video')} className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border ${ms.kind === 'video' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`} data-testid="media-kind-video">
            <Video className="w-3.5 h-3.5" /> Video
          </button>
        </div>
      </div>

      {/* Media prompt */}
      <div>
        <label className="text-xs font-bold text-[#64748B] uppercase block mb-1">Media prompt <span className="text-[#EF4444] normal-case font-normal">(required to publish)</span></label>
        {hasMedia ? (
          <div className="space-y-2">
            {ms.kind === 'video'
              ? <video src={ms.media_url} controls playsInline className="w-full rounded border border-[#E2E8F0]" data-testid="media-preview-video" />
              : <audio src={ms.media_url} controls className="w-full" data-testid="media-preview-audio" />}
            <div className="flex items-center gap-3">
              <label className="text-[11px] text-[#8B5CF6] hover:underline cursor-pointer" data-testid="media-replace">
                {uploading ? 'Uploading…' : 'Replace with a file'}
                <input type="file" className="hidden" accept={accept} disabled={uploading} onChange={(e) => uploadFile((e.target.files || [])[0])} data-testid="media-replace-input" />
              </label>
              <button type="button" className="text-[11px] text-[#D62B27] hover:underline" onClick={() => setUrl('')} data-testid="media-remove">Remove / re-record</button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed text-xs cursor-pointer hover:bg-[#F8FAFC] border-[#CBD5E1]" data-testid="media-upload">
              <Upload className="w-3.5 h-3.5 text-[#64748B]" />
              <span className="text-[#64748B]">{uploading ? 'Uploading…' : `Upload ${ms.kind} file (max ${ms.kind === 'video' ? 50 : 20}MB)`}</span>
              <input type="file" className="hidden" accept={accept} disabled={uploading} onChange={(e) => uploadFile((e.target.files || [])[0])} data-testid="media-upload-input" />
            </label>
            <p className="text-[10px] text-[#94A3B8] text-center">or</p>
            <BuilderMediaRecorder kind={ms.kind} onUploaded={setUrl} />
          </div>
        )}
      </div>

      {/* Answer format */}
      <div>
        <label className="text-xs font-bold text-[#64748B] uppercase block mb-2">How respondents answer</label>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setFormat('long_text')} className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border ${ms.answer_format === 'long_text' ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`} data-testid="media-answer-long-text">
            <FileText className="w-3.5 h-3.5" /> Long Text
          </button>
          <button type="button" onClick={() => setFormat('voice')} className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border ${ms.answer_format === 'voice' ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`} data-testid="media-answer-voice">
            <Mic className="w-3.5 h-3.5" /> Voice Response
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LOGIC RULES PANEL ───
function LogicRulesPanel({ question, questions, onUpdate }) {
  const rules = question.logic_rules || [];
  // Exclude the source question itself + non-answerable meta types (welcome
  // / thank-you) from selectable destinations. Section headers ARE allowed
  // as skip targets — jumping to a section is a valid intent.
  const otherQs = questions.filter(q => q.id !== question.id && q.type !== 'welcome' && q.type !== 'thank_you');
  // Precompute a 1-based order index so the destination dropdown reads
  // "3. What did you like most?" for reviewer clarity.
  const orderIndex = new Map();
  questions.forEach((q, i) => orderIndex.set(q.id, i + 1));

  const addRule = () => {
    const newRule = { id: crypto.randomUUID(), question_id: question.id, condition_question_id: otherQs[0]?.id || '', operator: 'equals', condition_value: '', action: 'show', target_question_id: null };
    onUpdate(question.id, { logic_rules: [...rules, newRule] });
  };

  const updateRule = (idx, field, val) => {
    const updated = [...rules];
    updated[idx] = { ...updated[idx], [field]: val };
    onUpdate(question.id, { logic_rules: updated });
  };

  const removeRule = (idx) => {
    onUpdate(question.id, { logic_rules: rules.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="logic-rules">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Conditional Logic</h4>
      {rules.map((rule, idx) => (
        <div key={rule.id} className="bg-[#F8F9FA] rounded-lg p-3 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-[#64748B] font-medium">Rule {idx + 1}</span>
            <button onClick={() => removeRule(idx)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex gap-1.5 items-center flex-wrap">
            <span className="text-[#64748B]">If</span>
            <select className="border rounded px-1.5 py-1 text-xs flex-1 min-w-0" value={rule.condition_question_id} onChange={(e) => updateRule(idx, 'condition_question_id', e.target.value)}>
              {otherQs.map(q => <option key={q.id} value={q.id}>{q.text?.slice(0, 30)}</option>)}
            </select>
          </div>
          <div className="flex gap-1.5 items-center flex-wrap">
            <select className="border rounded px-1.5 py-1 text-xs" value={rule.operator} onChange={(e) => updateRule(idx, 'operator', e.target.value)}>
              <option value="equals">equals</option>
              <option value="not_equals">not equals</option>
              <option value="greater_than">greater than</option>
              <option value="less_than">less than</option>
              <option value="contains">contains</option>
            </select>
            <Input value={rule.condition_value} onChange={(e) => updateRule(idx, 'condition_value', e.target.value)} className="text-xs h-7 flex-1" placeholder="Value" />
          </div>
          <div className="flex gap-1.5 items-center">
            <span className="text-[#64748B]">Then</span>
            <select className="border rounded px-1.5 py-1 text-xs" value={rule.action} onChange={(e) => updateRule(idx, 'action', e.target.value)} data-testid={`logic-action-${idx}`}>
              <option value="show">Show this question</option>
              <option value="hide">Hide this question</option>
              <option value="skip_to">Skip to question</option>
            </select>
          </div>
          {rule.action === 'skip_to' && (
            <div className="flex gap-1.5 items-center flex-wrap">
              <span className="text-[#64748B]">Skip to</span>
              <select
                className={`border rounded px-1.5 py-1 text-xs flex-1 min-w-0 ${!rule.target_question_id ? 'border-red-400' : ''}`}
                value={rule.target_question_id || ''}
                onChange={(e) => updateRule(idx, 'target_question_id', e.target.value || null)}
                data-testid={`logic-skip-target-${idx}`}
              >
                <option value="">Select destination question…</option>
                {otherQs.map(q => {
                  const num = orderIndex.get(q.id);
                  const label = q.text?.slice(0, 40) || '(untitled)';
                  return <option key={q.id} value={q.id}>{num ? `${num}. ${label}` : label}</option>;
                })}
              </select>
              {!rule.target_question_id && (
                <span className="text-[10px] text-red-500 w-full">A destination question is required for skip rules.</span>
              )}
            </div>
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addRule} className="w-full text-xs" data-testid="add-logic-rule-btn">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add Logic Rule
      </Button>
    </div>
  );
}

// ─── QR CODES PANEL ───
function QrCodesPanel({ qrCodes, onAssign, surveyId, allowPublicPreview }) {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Build a cache-busted URL so regenerated PNGs on disk always replace the
  // browser-cached copy, even though the stable filename hasn't changed.
  const qrSrc = (qr) => {
    if (!qr?.qr_image_url) return '';
    const bust = qr._cacheBust || qr.updated_at || Date.now();
    const sep = qr.qr_image_url.includes('?') ? '&' : '?';
    return `${baseUrl}${qr.qr_image_url}${sep}v=${encodeURIComponent(bust)}`;
  };

  const downloadQr = (qr) => {
    const link = document.createElement('a');
    link.href = qrSrc(qr);
    link.download = `qr_${qr.branch_name?.replace(/\s+/g, '_')}.png`;
    link.click();
  };

  return (
    <div className="space-y-4" data-testid="qr-codes-panel">
      {qrCodes.length === 0 ? (
        <div className="text-center py-8">
          <QrCode className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-sm text-[#64748B] mb-4">No QR codes yet. Assign branches to generate QR codes.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {qrCodes.map((qr) => (
            <div key={qr.id || qr.branch_id} className="flex items-center gap-3 p-3 bg-[#F8F9FA] rounded-lg">
              <img src={qrSrc(qr)} alt="QR" className="w-16 h-16 rounded" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A]">{qr.branch_name}</p>
                <p className="text-[10px] text-[#94A3B8] break-all">{qr.qr_code_url}</p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => downloadQr(qr)} data-testid={`download-qr-${qr.branch_id}`} title="Download QR PNG">
                  <Download className="w-4 h-4" />
                </Button>
                {allowPublicPreview && qr.qr_code_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[10px] h-6 px-2 text-[#1A3E6F] hover:bg-[#E0E7FF]"
                    onClick={() => {
                      const previewUrl = `${qr.qr_code_url}/preview`;
                      navigator.clipboard?.writeText(previewUrl).then(
                        () => toast.success('Preview link copied'),
                        () => toast.error('Could not copy — long-press the URL above to copy manually.'),
                      );
                    }}
                    data-testid={`copy-preview-${qr.branch_id}`}
                    title="Copy public preview URL (for mystery shoppers)"
                  >
                    Copy preview link
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Button variant="outline" className="w-full" onClick={onAssign} data-testid="assign-branches-btn">
        <Plus className="w-4 h-4 mr-2" /> Assign Branches
      </Button>
    </div>
  );
}
